import { useEffect, useState, useRef, useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { getApiError } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import {
  MessageSquare, Send, Users, User, Search, Plus, Check, CheckCheck,
  Loader2, Paperclip, X, FileText, Image as ImageIcon, FileSpreadsheet, File,
  Hash, MessageCircle, ChevronRight, Video, ZoomIn, PanelRightOpen, Settings, Trash2,
  Bell, BellOff, BellRing, Maximize2, Pencil, Search as SearchIcon, PhoneCall, Activity,
  CornerUpLeft,
} from 'lucide-react';
import { useChatNotification } from '../context/ChatNotificationContext';
import { usePermissions } from '../context/usePermissions';
import { useDraft, useDraftKeys, readDraft } from '../hooks/useDraft';
import EmojiPicker from 'emoji-picker-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// When a status was set and by whom, for the badge tooltip. Completion is the later
// event, so it wins when both are present.
function txBadgeWho(msg) {
  const who = msg.tx_completed_by_name || msg.tx_processed_by_name;
  const when = msg.tx_completed_at || msg.tx_processed_at;
  if (!who && !when) return null;
  let stamp = '';
  if (when) {
    const d = new Date(when);
    if (!isNaN(d)) {
      stamp = d.toLocaleString(undefined, {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    }
  }
  return { who, stamp };
}

function txBadgeTip(msg) {
  const i = txBadgeWho(msg);
  if (!i) return undefined;
  const verb = msg.tx_completed_by ? 'Completed' : 'Processed';
  return `${verb}${i.who ? ` by ${i.who}` : ''}${i.stamp ? ` · ${i.stamp}` : ''}`;
}

// Status badge for auto-posted transaction cards — reflects the furthest state reached.
// Hovering shows who moved it there and when; the older cards that predate the
// attribution fields simply have no tooltip.
function TxBadge({ msg }) {
  const st = msg.tx_status;
  let label = '⏳ Pending', cls = 'bg-amber-100 text-amber-700', verb = 'Set';
  if (st === 'rejected') { label = '❌ Rejected'; cls = 'bg-red-100 text-red-700'; verb = 'Rejected'; }
  else if (st === 'completed' || msg.tx_completed_by) { label = '✅ Completed'; cls = 'bg-emerald-100 text-emerald-700'; verb = 'Completed'; }
  else if (st === 'approved') { label = '✅ Approved'; cls = 'bg-green-100 text-green-700'; verb = 'Approved'; }
  else if (msg.tx_processed_by) { label = '⚙️ Processing'; cls = 'bg-blue-100 text-blue-700'; verb = 'Processed'; }

  const info = txBadgeWho(msg);
  const tip = info
    ? `${verb}${info.who ? ` by ${info.who}` : ''}${info.stamp ? ` · ${info.stamp}` : ''}`
    : undefined;

  return (
    <span title={tip}
      className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${cls} ${tip ? 'cursor-help' : ''}`}>
      {label}
    </span>
  );
}

// Reaction chips — shown ABOVE the message; click a chip to toggle your reaction.
function ReactionChips({ reactions, onReact, currentUserId }) {
  const entries = Object.entries(reactions || {}).filter(([, arr]) => (arr || []).length);
  if (!entries.length) return null;
  return (
    <div className="flex items-center gap-1 mb-1 flex-wrap">
      {entries.map(([emoji, arr]) => {
        const mine = (arr || []).some(r => r.user_id === currentUserId);
        return (
          <button key={emoji} type="button" onClick={() => onReact(emoji)}
            title={(arr || []).map(r => r.name).filter(Boolean).join(', ')}
            className={`text-[11px] px-1.5 py-0.5 rounded-full border transition-colors ${mine
              ? 'bg-blue-100 border-blue-300 text-blue-700'
              : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'}`}>
            {emoji} {(arr || []).length}
          </button>
        );
      })}
    </div>
  );
}

// Single "add reaction" button — shown BELOW the message; opens the full emoji picker.
function ReactionAdder({ onReact }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div className="relative mt-0.5 clear-both">
      <button type="button" title="Add reaction" onClick={() => setPickerOpen(o => !o)}
        className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-700 border border-slate-200 hover:border-slate-300 rounded-full px-2 py-0.5 hover:bg-slate-100">
        <span className="text-[13px] leading-none">🙂</span> React
      </button>
      {pickerOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setPickerOpen(false)} />
          <div className="absolute bottom-8 left-0 z-50">
            <EmojiPicker onEmojiClick={(ed) => { onReact(ed.emoji); setPickerOpen(false); }}
              theme="auto" lazyLoadEmojis width={300} height={380}
              previewConfig={{ showPreview: false }} skinTonesDisabled />
          </div>
        </>
      )}
    </div>
  );
}

// Status tags for workflow channels (e.g. #scalping-check). Rendered as colored
// pills like TxBadge; multiple can be active at once.
const TAG_CHANNELS = ['scalping-check'];
const TAG_META = [
  { key: 'Approved',  icon: '✅', cls: 'bg-emerald-100 text-emerald-700' },
  { key: 'Processed', icon: '⚙️', cls: 'bg-blue-100 text-blue-700' },
  { key: 'Hold',      icon: '✋', cls: 'bg-amber-100 text-amber-700' },
  { key: 'Rejected',  icon: '❌', cls: 'bg-red-100 text-red-700' },
  { key: 'Cancelled', icon: '🚫', cls: 'bg-rose-100 text-rose-700' },
  { key: 'Pending',   icon: '⏳', cls: 'bg-slate-100 text-slate-600' },
  { key: 'Query',     icon: '❓', cls: 'bg-purple-100 text-purple-700' },
];

// ── Pure formatting/render helpers, hoisted to module scope ────────────────────
// These previously lived inside the main component and were recreated on every
// render; since none of them depend on component state, defining them once here
// makes their identity stable for free and lets a memoized row component call
// them directly without needing them threaded through as props.
const IST = 'Asia/Kolkata';  // All chat times are shown in IST (Asia/Kolkata)

const formatTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const now = new Date();
  const diff = Math.floor((now - dt) / 86400000);
  if (diff === 0) return dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: IST });
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return dt.toLocaleDateString('en-US', { weekday: 'short', timeZone: IST });
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: IST });
};

const formatFullTime = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: IST });
};

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: IST });
};

// Compact IST date for the narrow grouped-message gutter: "17 Jul"
const formatShortDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: IST });
};

// Full IST date + time (for tooltips): "11 Jul 2026, 08:46 PM IST"
const formatISTFull = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true, timeZone: IST,
  }) + ' IST';
};

const getDateLabel = (d) => {
  const dt = new Date(d);
  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  if (dt.toDateString() === today.toDateString()) return 'Today';
  if (dt.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};

const getInitials = (n) => !n ? '?' : n.split(' ').map(s => s[0]).join('').toUpperCase().slice(0, 2);

const formatFileSize = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

const isImage = (fn, ct) => {
  const ext = (fn || '').split('.').pop().toLowerCase();
  return (ct || '').startsWith('image/') || ['jpg','jpeg','png','gif','webp'].includes(ext);
};
const isVideo = (fn, ct) => {
  const ext = (fn || '').split('.').pop().toLowerCase();
  return (ct || '').startsWith('video/') || ['mp4','mov','webm','avi'].includes(ext);
};
const getFileIcon = (fn, ct) => {
  const ext = (fn || '').split('.').pop().toLowerCase();
  if (isImage(fn, ct)) return <ImageIcon className="w-4 h-4 text-green-500" />;
  if (isVideo(fn, ct)) return <Video className="w-4 h-4 text-purple-500" />;
  if (ext === 'pdf' || ct === 'application/pdf') return <FileText className="w-4 h-4 text-red-400" />;
  if (['xlsx','xls','csv'].includes(ext)) return <FileSpreadsheet className="w-4 h-4 text-emerald-400" />;
  return <File className="w-4 h-4 text-slate-400" />;
};

// Renders image/video/other attachments for a message bubble. onImageClick opens
// the lightbox — passed explicitly since this now lives outside the component and
// can't close over its setLightboxUrl state setter.
const renderAttachmentsBase = (attachments, isSelf, onImageClick) => {
  if (!attachments?.length) return null;
  const imgs = attachments.filter(a => isImage(a.filename, a.content_type));
  const vids = attachments.filter(a => !isImage(a.filename, a.content_type) && isVideo(a.filename, a.content_type));
  const others = attachments.filter(a => !isImage(a.filename, a.content_type) && !isVideo(a.filename, a.content_type));
  return (
    <div className="mt-1.5 space-y-1.5">
      {imgs.length > 0 && (
        <div className={`grid gap-1 ${imgs.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`} style={{ maxWidth: 280 }}>
          {imgs.map((img, i) => (
            <div key={i} className="relative group cursor-pointer rounded-lg overflow-hidden" onClick={() => onImageClick(img.url)}>
              <img src={img.url} alt={img.filename} loading="lazy" className="w-full object-cover" style={{ maxHeight: 180 }} />
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <ZoomIn className="w-6 h-6 text-white drop-shadow" />
              </div>
            </div>
          ))}
        </div>
      )}
      {vids.map((v, i) => (
        <video key={i} src={v.url} controls preload="none" className="rounded-lg w-full" style={{ maxWidth: 280, maxHeight: 180 }} />
      ))}
      {others.map((f, i) => (
        <a key={i} href={f.url} target="_blank" rel="noopener noreferrer"
          className={`flex items-center gap-2 p-2 rounded-lg ${isSelf ? 'bg-blue-500/20 hover:bg-blue-500/30' : 'bg-slate-100 hover:bg-slate-200'}`}>
          {getFileIcon(f.filename, f.content_type)}
          <div className="min-w-0">
            <p className="text-xs font-medium truncate">{f.filename}</p>
            <p className={`text-xs ${isSelf ? 'text-blue-300' : 'text-slate-400'}`}>{formatFileSize(f.size)}</p>
          </div>
        </a>
      ))}
    </div>
  );
};

// Pending-attachment thumbnail strip shown above the composer while drafting a
// message. Hoisted to module scope (it used to be redefined inside the main
// component's render body — a fresh component identity every render, which made
// React remount this subtree, including re-decoding images, on every keystroke).
// Creates one object URL per file and revokes it on cleanup/file-list change
// instead of leaking a fresh, never-revoked blob URL on every render.
function FilePreviewStrip({ files, onRemove }) {
  const [urls, setUrls] = useState([]);
  useEffect(() => {
    const next = files.map(f => (isImage(f.name, f.type) ? URL.createObjectURL(f) : null));
    setUrls(next);
    return () => { next.forEach(u => u && URL.revokeObjectURL(u)); };
  }, [files]);

  if (!files.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-2 p-2 bg-slate-50 rounded-lg border">
      {files.map((f, i) => (
        <div key={i} className="relative group">
          {isImage(f.name, f.type) ? (
            <div className="w-12 h-12 rounded overflow-hidden border">
              {urls[i] && <img src={urls[i]} alt={f.name} className="w-full h-full object-cover" />}
            </div>
          ) : isVideo(f.name, f.type) ? (
            <div className="w-12 h-12 rounded bg-purple-50 border border-purple-200 flex items-center justify-center">
              <Video className="w-5 h-5 text-purple-500" />
            </div>
          ) : (
            <div className="flex items-center gap-1 px-2 py-1.5 bg-white border rounded-lg max-w-[100px]">
              {getFileIcon(f.name, f.type)}
              <p className="text-xs truncate">{f.name}</p>
            </div>
          )}
          <button onClick={() => onRemove(i)}
            className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <X className="w-2.5 h-2.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

const DateDivider = ({ date }) => (
  <div className="flex items-center gap-3 my-4 px-4">
    <div className="flex-1 h-px bg-slate-200" />
    <span className="text-xs text-slate-400 font-medium whitespace-nowrap">{getDateLabel(date)}</span>
    <div className="flex-1 h-px bg-slate-200" />
  </div>
);

// A single channel message row. Wrapped in React.memo so that a parent re-render
// (typing in the composer, opening a thread, a WS tick for an unrelated message,
// hovering) only re-renders the ONE row that actually changed instead of every
// loaded message — this was the dominant cost behind the measured per-keystroke
// and per-click main-thread freezes (each ~1-2s with 200 rows mounted). All
// callback props are stable (useCallback in the parent, taking msg as a call-time
// argument rather than a closure) so the default shallow-prop comparison works.
const ChannelMessageRow = memo(function ChannelMessageRow({
  msg, currentUserId, isAdmin, isEditing, editText, onEditTextChange, highlighted,
  tagChannelActive, canProcessTx, onOpenThread, onReact, onTag, onStartEdit,
  onSaveEdit, onCancelEdit, onDelete, onTxProcess, onTxComplete, onNavigateToTx, onImageClick,
}) {
  const isSelf = msg.sender_id === currentUserId;
  return (
    <div id={`chmsg-${msg.msg_id}`} className="scroll-mt-4">
      {msg.showDateDivider && <DateDivider date={msg.created_at} />}
      <div className={`group flex gap-3 px-4 py-0.5 transition-colors duration-700 ${highlighted ? 'bg-blue-100' : 'hover:bg-slate-50/60'}`}>
        {/* Avatar or grouped spacer */}
        <div className="w-9 shrink-0 mt-1">
          {!msg.isGrouped ? (
            <Avatar className="w-9 h-9">
              <AvatarFallback className={`text-xs font-medium text-white ${isSelf ? 'bg-gradient-to-br from-blue-500 to-blue-700' : 'bg-gradient-to-br from-indigo-500 to-purple-600'}`}>
                {getInitials(msg.sender_name)}
              </AvatarFallback>
            </Avatar>
          ) : (
            <span title={formatISTFull(msg.created_at)}
              className="text-[9px] text-slate-400 block text-right leading-tight pt-2 whitespace-nowrap -ml-3">
              {formatShortDate(msg.created_at)}<br />{formatFullTime(msg.created_at)}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0 pb-1">
          {/* Name + time header (first in group only) */}
          {!msg.isGrouped && (
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-sm font-bold text-slate-900">{isSelf ? 'You' : msg.sender_name}</span>
              <span className="text-xs text-slate-400 whitespace-nowrap" title={formatISTFull(msg.created_at)}>{formatDate(msg.created_at)}</span>
              <button onClick={() => onOpenThread(msg)}
                className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-xs text-slate-400 hover:text-blue-600 bg-white hover:bg-blue-50 rounded px-2 py-0.5 border border-slate-200 hover:border-blue-300 shadow-sm">
                <MessageCircle className="w-3 h-3" /> Reply
              </button>
            </div>
          )}
          {/* Grouped reply button on hover */}
          {msg.isGrouped && (
            <button onClick={() => onOpenThread(msg)}
              className="opacity-0 group-hover:opacity-100 transition-opacity float-right flex items-center gap-1 text-xs text-slate-400 hover:text-blue-600 bg-white hover:bg-blue-50 rounded px-2 py-0.5 border border-slate-200 hover:border-blue-300 shadow-sm">
              <MessageCircle className="w-3 h-3" /> Reply
            </button>
          )}
          {!msg.deleted && (
            <ReactionChips reactions={msg.reactions} currentUserId={currentUserId}
              onReact={(emoji) => onReact(msg, emoji, 'channel')} />
          )}
          {!msg.deleted && tagChannelActive && (
            <TagChips tags={msg.tags} onTag={(tag) => onTag(msg, tag)} currentUserId={currentUserId} />
          )}
          {/* Bubble (text + attachments together) */}
          {msg.deleted ? (
            <div className="inline-block rounded-2xl px-4 py-2 max-w-lg bg-slate-100 text-slate-400 italic text-sm">🚫 This message was deleted</div>
          ) : isEditing ? (
            <div className="flex flex-col gap-1 max-w-lg">
              <textarea value={editText} onChange={e => onEditTextChange(e.target.value)} rows={2}
                className="w-full text-sm rounded-lg border border-blue-300 px-3 py-2 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-400" autoFocus />
              <div className="flex gap-2">
                <button onClick={() => onSaveEdit(msg.msg_id)} className="text-xs bg-blue-600 text-white rounded px-2 py-0.5 hover:bg-blue-700">Save</button>
                <button onClick={onCancelEdit} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
              </div>
            </div>
          ) : (msg.content || msg.attachments?.length > 0) && (
            <div className={`relative group/msg inline-block rounded-2xl px-4 py-2.5 max-w-lg ${
              isSelf
                ? 'bg-blue-600 text-white rounded-tl-sm'
                : 'bg-slate-100 text-slate-800 rounded-tl-sm'
            }`}>
              {(isSelf || isAdmin) && (
                <div className="absolute -top-2 -right-2 opacity-0 group-hover/msg:opacity-100 transition-opacity flex gap-0.5 bg-white border border-slate-200 rounded-full shadow-sm px-1 z-10">
                  {isSelf && <button title="Edit" onClick={() => onStartEdit(msg.msg_id, msg.content)} className="p-1 text-slate-500 hover:text-blue-600"><Pencil className="w-3 h-3" /></button>}
                  <button title="Delete" onClick={() => onDelete(msg.msg_id)} className="p-1 text-slate-500 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                </div>
              )}
              {msg.content && (
                <p className={`text-sm whitespace-pre-wrap leading-relaxed ${isSelf ? 'text-white' : 'text-slate-800'}`}>
                  {renderMentionedContent(msg.content, msg.mentions)}
                </p>
              )}
              {msg.attachments?.length > 0 && renderAttachmentsBase(msg.attachments, isSelf, onImageClick)}
              {msg.is_tx_bot && (
                <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                  <TxBadge msg={msg} />
                  {msg.tx_reference && (
                    <button type="button" onClick={() => onNavigateToTx(msg.tx_reference)}
                      className={`text-[11px] underline ${isSelf ? 'text-blue-100' : 'text-blue-600'} hover:opacity-80`}>
                      View transaction →
                    </button>
                  )}
                  {msg.tx_type === 'withdrawal' && !msg.tx_processed_by && !msg.tx_direct && canProcessTx && (
                    <button type="button" onClick={() => onTxProcess(msg)}
                      className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-600 text-white hover:bg-amber-700">Process</button>
                  )}
                  {msg.tx_processed_by && (
                    <span title={txBadgeTip(msg)} className="text-[11px] text-emerald-600 font-medium cursor-help">🟢 Processed{msg.tx_processed_by_name ? ` by ${msg.tx_processed_by_name}` : ''}</span>
                  )}
                  {msg.tx_owner_id === currentUserId && !msg.tx_completed_by && (
                    <button type="button" onClick={() => onTxComplete(msg)}
                      className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-600 text-white hover:bg-blue-700">Complete</button>
                  )}
                  {msg.tx_completed_by && (
                    <span title={txBadgeTip(msg)} className="text-[11px] text-green-600 font-medium cursor-help">✅ Completed{msg.tx_completed_by_name ? ` by ${msg.tx_completed_by_name}` : ''}</span>
                  )}
                </div>
              )}
              {msg.edited && <span className={`text-[10px] ml-1 ${isSelf ? 'text-blue-200' : 'text-slate-400'}`}>(edited)</span>}
            </div>
          )}
          {!msg.deleted && (
            <div className="flex items-center gap-1.5 clear-both">
              <ReactionAdder onReact={(emoji) => onReact(msg, emoji, 'channel')} />
              {tagChannelActive && (
                <TagBar tags={msg.tags} onTag={(tag) => onTag(msg, tag)} currentUserId={currentUserId} />
              )}
            </div>
          )}
          {/* Thread reply count */}
          {msg.reply_count > 0 && (
            <button onClick={() => onOpenThread(msg)}
              className="flex items-center gap-1.5 mt-1.5 text-xs text-blue-600 hover:underline font-medium clear-both">
              <MessageCircle className="w-3.5 h-3.5" />
              {msg.reply_count} {msg.reply_count === 1 ? 'reply' : 'replies'}
              <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

const validateChannelFile = (f) => {
  const max = f.type?.startsWith('video/') ? 104857600 : 20971520;
  if (f.size > max) { toast.error(`${f.name} exceeds ${f.type?.startsWith('video/') ? '100MB' : '20MB'} limit`); return false; }
  return true;
};

// Channel message composer, isolated with its own local state (msg/files/sending).
// Previously this state lived in the main Messages component, so every keystroke
// re-rendered the entire (up to hundreds of rows) message list along with it — this
// was the other half of the measured typing lag (the other half being the unmemoized
// row rendering fixed by ChannelMessageRow above). Now a keystroke here only
// re-renders this small composer subtree; onSent notifies the parent once, on send.
function ChannelComposer({ channelId, channelName, members, onSent, getAuthHeaders }) {
  const { text: msg, setText: setMsg, clearDraft, setMentions, getMentions } = useDraft(channelId ? `channel:${channelId}` : null);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const mention = useMentionOverlay(query => filterMentionCandidates(members, query));

  // Switching channels keeps the draft (useDraft swaps it in); only the attachments,
  // which cannot be persisted, are dropped.
  useEffect(() => {
    setFiles([]);
    mention.loadMentions(getMentions());
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  // Every text change re-captures the mention set, so the debounced draft write
  // always stores text and mentions as a matched pair. applyMention has already run
  // by the time this fires on both the click and the Enter/Tab path.
  const updateMsg = (next) => {
    setMentions(mention.getMentionsForSend());
    setMsg(next);
  };

  const handleFileSelect = (e) => {
    const picked = Array.from(e.target.files || []).filter(validateChannelFile);
    setFiles(prev => [...prev, ...picked]); e.target.value = '';
  };
  const handlePaste = (e) => {
    const items = e.clipboardData?.items || []; const pasted = [];
    for (const item of items) { if (item.kind === 'file') { const f = item.getAsFile(); if (f && validateChannelFile(f)) pasted.push(f); } }
    if (pasted.length) setFiles(prev => [...prev, ...pasted]);
  };
  const handleSend = async () => {
    if ((!msg.trim() && !files.length) || sending) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('content', msg);
      fd.append('mentions', JSON.stringify(mention.getMentionsForSend()));
      files.forEach(f => fd.append('files', f));
      const headers = { ...getAuthHeaders() }; delete headers['Content-Type'];
      const r = await fetch(`${API_URL}/api/channels/${channelId}/messages`, { method: 'POST', headers, body: fd });
      if (r.ok) {
        const sentMsg = await r.json();
        clearDraft(); setFiles([]);
        mention.resetMentions();
        if (textareaRef.current) textareaRef.current.style.height = 'auto';
        onSent(sentMsg);
      } else toast.error(await getApiError(r));
    } catch (e) { toast.error(e?.message || 'Something went wrong'); } finally { setSending(false); }
  };

  return (
    <div className="px-4 py-3 border-t shrink-0">
      <FilePreviewStrip files={files} onRemove={i => setFiles(prev => prev.filter((_, idx) => idx !== i))} />
      <div className="relative flex items-end gap-2 bg-slate-100/70 rounded-xl px-3 py-2 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-400/40 transition-all border border-transparent focus-within:border-blue-300">
        {mention.trigger && (
          <MentionDropdown candidates={mention.candidates} activeIndex={mention.activeIndex}
            onSelect={u => {
              const result = mention.applyMention(u, msg);
              if (result) {
                updateMsg(result.text);
                requestAnimationFrame(() => { textareaRef.current?.focus(); textareaRef.current?.setSelectionRange(result.caret, result.caret); });
              }
            }} />
        )}
        <input ref={fileInputRef} type="file" className="hidden" multiple accept="image/*,video/*,.pdf,.xlsx,.xls,.csv,.doc,.docx" onChange={handleFileSelect} />
        <button className="text-slate-400 hover:text-blue-600 transition-colors shrink-0" onClick={() => fileInputRef.current?.click()} title="Attach files">
          <Paperclip className="w-5 h-5" />
        </button>
        <Textarea
          ref={textareaRef}
          value={msg}
          onChange={e => {
            updateMsg(e.target.value);
            mention.handleChange(e.target.value, e.target.selectionStart);
            e.target.style.height = 'auto';
            e.target.style.height = Math.min(e.target.scrollHeight, 128) + 'px';
          }}
          onPaste={handlePaste}
          onKeyDown={e => {
            if (mention.handleKeyDown(e, msg, updateMsg, textareaRef.current)) return;
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
          }}
          placeholder={`Message #${channelName}`}
          className="flex-1 resize-none text-sm border-0 p-0 focus-visible:ring-0 shadow-none bg-transparent overflow-y-hidden"
          rows={1}
          style={{ minHeight: 22, maxHeight: 128 }}
        />
        <button
          onClick={handleSend}
          disabled={(!msg.trim() && !files.length) || sending}
          className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
      <p className="text-xs text-slate-400 mt-1 ml-1">Enter to send · Shift+Enter for new line · Paste images · @ to mention</p>
    </div>
  );
}

// ── @mention support ────────────────────────────────────────────────────────────
// Mentions are tracked as {user_id, name} pairs captured at send time (from whoever
// was actually picked in the autocomplete), not re-parsed from the raw text on
// display — that keeps rendering and "was I mentioned" notifications correct even if
// the mentioned person's name later changes or they leave the channel.

// Detects an in-progress "@word" trigger immediately before the caret, so a caller
// can show an autocomplete dropdown. Returns null once a space closes the token, or
// the "@" isn't preceded by start-of-text/whitespace (avoids matching an email
// address or similar mid-word "@").
function detectMentionTrigger(text, caret) {
  const uptoCaret = text.slice(0, caret);
  const at = uptoCaret.lastIndexOf('@');
  if (at === -1) return null;
  const between = uptoCaret.slice(at + 1);
  if (/[\s\n]/.test(between)) return null;
  if (at > 0 && !/\s/.test(uptoCaret[at - 1])) return null;
  return { start: at, query: between };
}

// Inserts "@Name " at the trigger position, replacing the partial "@query" typed so far.
function insertMention(text, trigger, name) {
  const before = text.slice(0, trigger.start);
  const after = text.slice(trigger.start + 1 + trigger.query.length);
  const inserted = `@${name} `;
  return { text: `${before}${inserted}${after}`, caret: (before + inserted).length };
}

// Renders message content with @Name mentions highlighted, using the exact names
// captured in `mentions` ([{user_id, name}]) rather than re-detecting them from text.
function renderMentionedContent(content, mentions) {
  if (!content || !mentions?.length) return content;
  const names = [...new Set(mentions.map(m => m.name).filter(Boolean))].sort((a, b) => b.length - a.length);
  if (!names.length) return content;
  const pattern = new RegExp(`@(${names.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
  return content.split(pattern).map((part, i) => (
    i % 2 === 1
      ? <span key={i} className="font-semibold text-blue-600 bg-blue-50 rounded px-1">@{part}</span>
      : part
  ));
}

// Filters a {user_id, name} candidate list by the in-progress "@query" (case-
// insensitive prefix match on name, falling back to substring match), capped to a
// manageable dropdown size. Empty query -> first N candidates, so "@" alone shows
// something useful rather than an empty dropdown.
function filterMentionCandidates(candidates, query) {
  if (!candidates?.length) return [];
  const q = (query || '').toLowerCase();
  if (!q) return candidates.slice(0, 6);
  const starts = candidates.filter(u => u.name?.toLowerCase().startsWith(q));
  const contains = candidates.filter(u => !u.name?.toLowerCase().startsWith(q) && u.name?.toLowerCase().includes(q));
  return [...starts, ...contains].slice(0, 6);
}

// Autocomplete dropdown shown above a composer while a mention is in progress.
function MentionDropdown({ candidates, activeIndex, onSelect }) {
  if (!candidates.length) return null;
  return (
    <div className="absolute bottom-full left-0 mb-1 w-64 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1">
      {candidates.map((u, i) => (
        <button key={u.user_id} type="button"
          onMouseDown={e => { e.preventDefault(); onSelect(u); }}
          className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${i === activeIndex ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-50'}`}>
          <span className="w-5 h-5 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-[10px] flex items-center justify-center shrink-0 font-medium">
            {getInitials(u.name)}
          </span>
          <span className="truncate">{u.name}</span>
        </button>
      ))}
    </div>
  );
}

// Encapsulates the @mention autocomplete overlay for a single plain-textarea composer.
// Deliberately does NOT own the draft text itself (each composer already has its own
// text state from before this feature existed) — it's a thin layer on top: callers
// feed it the current text + caret on change, and call its handlers from their own
// onChange/onKeyDown. `getCandidates(query)` returns the filtered {user_id, name} list
// to show for the in-progress query (empty query -> a sensible default list).
function useMentionOverlay(getCandidates) {
  const [trigger, setTrigger] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const mentionedRef = useRef(new Map()); // user_id -> name, for the current draft

  const candidates = trigger ? getCandidates(trigger.query) : [];

  const handleChange = (text, caret) => {
    setTrigger(detectMentionTrigger(text, caret));
    setActiveIndex(0);
  };

  const applyMention = (user, text) => {
    if (!trigger) return null;
    const result = insertMention(text, trigger, user.name);
    mentionedRef.current.set(user.user_id, user.name);
    setTrigger(null);
    return result;
  };

  // Returns true if the key was consumed by the dropdown (caller should return early
  // and skip its own Enter-to-send / newline handling for this keystroke).
  const handleKeyDown = (e, text, setText, textareaEl) => {
    if (!trigger || !candidates.length) return false;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => (i + 1) % candidates.length); return true; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => (i - 1 + candidates.length) % candidates.length); return true; }
    if (e.key === 'Escape') { e.preventDefault(); setTrigger(null); return true; }
    if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
      e.preventDefault();
      const picked = candidates[activeIndex] || candidates[0];
      const result = applyMention(picked, text);
      if (result) {
        setText(result.text);
        requestAnimationFrame(() => { textareaEl?.focus(); textareaEl?.setSelectionRange(result.caret, result.caret); });
      }
      return true;
    }
    return false;
  };

  const getMentionsForSend = () => Array.from(mentionedRef.current, ([user_id, name]) => ({ user_id, name }));
  const resetMentions = () => { mentionedRef.current = new Map(); };
  // Rehydrate from a restored draft, so an "@Sara" that came back from storage still
  // sends as a real mention instead of decaying into plain text.
  const loadMentions = (list) => {
    mentionedRef.current = new Map((list || []).map(m => [m.user_id, m.name]));
  };

  return { trigger, candidates, activeIndex, handleChange, applyMention, handleKeyDown, getMentionsForSend, resetMentions, loadMentions };
}

// Active tag pills — shown ABOVE the message (styled like the status badge). Only the
// user who ADDED a tag can remove it; for everyone else the pill is read-only (locked).
function TagChips({ tags, onTag, currentUserId }) {
  const active = TAG_META.filter(t => tags && tags[t.key]);
  if (!active.length) return null;
  return (
    <div className="flex items-center gap-1 mb-1 flex-wrap">
      {active.map(t => {
        const meta = tags[t.key] || {};
        const by = meta.by;
        const mine = !meta.by_id || meta.by_id === currentUserId; // legacy tags stay editable
        if (!mine) {
          return (
            <span key={t.key}
              title={`${t.key}${by ? ' — by ' + by : ''} · only ${by || 'the tagger'} can change this`}
              className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${t.cls} opacity-90 cursor-default inline-flex items-center gap-0.5`}>
              {t.icon} {t.key} <span className="text-[9px] opacity-70">🔒</span>
            </span>
          );
        }
        return (
          <button key={t.key} type="button" onClick={() => onTag(t.key)}
            title={`${t.key}${by ? ' — by ' + by : ''} · click to remove`}
            className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${t.cls} hover:opacity-75 transition-opacity`}>
            {t.icon} {t.key}
          </button>
        );
      })}
    </div>
  );
}

// "Tag" button — shown BELOW the message; opens a menu of all status tags (multi-select
// toggle). A tag set by someone else is locked (can't be toggled off by others).
function TagBar({ tags, onTag, currentUserId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative mt-0.5 inline-block clear-both align-top">
      <button type="button" title="Set status tags" onClick={() => setOpen(o => !o)}
        className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-700 border border-slate-200 hover:border-slate-300 rounded-full px-2 py-0.5 hover:bg-slate-100">
        <span className="text-[13px] leading-none">🏷</span> Tag
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute bottom-8 left-0 z-50 bg-white border border-slate-200 rounded-lg shadow-lg p-1.5 flex flex-col gap-1 w-40">
            {TAG_META.map(t => {
              const meta = (tags && tags[t.key]) || null;
              const on = !!meta;
              const lockedByOther = on && meta.by_id && meta.by_id !== currentUserId;
              return (
                <button key={t.key} type="button" disabled={lockedByOther}
                  onClick={() => { if (!lockedByOther) onTag(t.key); }}
                  title={lockedByOther ? `Set by ${meta.by || 'someone else'} — only they can change it` : undefined}
                  className={`flex items-center justify-between text-[12px] font-semibold px-2 py-1 rounded-md transition-colors ${on ? t.cls : 'text-slate-500 hover:bg-slate-100'} ${lockedByOther ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <span>{t.icon} {t.key}</span>
                  {on && <span className="text-[11px] leading-none">{lockedByOther ? '🔒' : '✓'}</span>}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// Nudge shown inside Messages when browser notifications aren't enabled yet.
function NotifyBanner({ onEnable }) {
  const [perm, setPerm] = useState(() => ('Notification' in window) ? Notification.permission : 'unsupported');
  useEffect(() => {
    const check = () => { if ('Notification' in window) setPerm(Notification.permission); };
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => { window.removeEventListener('focus', check); document.removeEventListener('visibilitychange', check); };
  }, []);
  if (perm === 'granted' || perm === 'unsupported') return null;
  const blocked = perm === 'denied';
  const enable = async () => {
    if (perm === 'default') { try { setPerm(await Notification.requestPermission()); } catch { /* */ } }
    onEnable?.();
  };
  return (
    <div className={`shrink-0 mb-3 flex items-center gap-3 rounded-lg border px-3 py-2 text-sm ${blocked ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
      <span className="text-lg leading-none">🔔</span>
      <span className="flex-1">
        {blocked
          ? 'Notifications are blocked. Enable them for this site in your browser settings (address-bar 🔒/ⓘ → Notifications → Allow) to get message alerts.'
          : 'Turn on browser notifications so you never miss a new message.'}
      </span>
      {!blocked && (
        <button onClick={enable} className="shrink-0 rounded-md bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-amber-700">
          Turn on
        </button>
      )}
    </div>
  );
}

export default function Messages({ fullscreen = false }) {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);

  // DM state
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const selectedConversationKey = selectedConversation ? `dm:${selectedConversation.user_id}` : null;
  // Conversation keys that currently hold a draft, for the sidebar indicator.
  const draftKeys = useDraftKeys();
  // Draft-backed per recipient. Previously this was plain state that nothing reset
  // on conversation switch, so text typed to one person stayed in the box when you
  // clicked another - one Enter away from sending it to the wrong person.
  const { text: newMessage, setText: setNewMessage, clearDraft: clearDmDraft,
          setMentions: setDmDraftMentions, getMentions: getDmDraftMentions } =
    useDraft(selectedConversationKey);
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const fileInputRef = useRef(null);
  const dmTextareaRef = useRef(null);
  // DM mentions can reference anyone (not just the other party in the conversation) -
  // e.g. "ask @manager about this" - so the candidate source is the full users list.
  const dmMention = useMentionOverlay(query => filterMentionCandidates(users, query));
  const [newChatDialog, setNewChatDialog] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState('');

  // Admin state
  const [viewMode, setViewMode] = useState('my');
  const [allConversations, setAllConversations] = useState([]);
  const [selectedAllConversation, setSelectedAllConversation] = useState(null);
  const [allMessages, setAllMessages] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  // Activity feed (unified view of every channel)
  const [activityItems, setActivityItems] = useState([]);
  const [activityChannels, setActivityChannels] = useState([]);
  const [activityFilter, setActivityFilter] = useState('');   // channel_id; '' = all channels
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const [activityHasMore, setActivityHasMore] = useState(false);
  const [activityReplyId, setActivityReplyId] = useState(null);   // msg_id whose inline reply box is open
  const [activityReplyText, setActivityReplyText] = useState('');
  const [activityReplySending, setActivityReplySending] = useState(false);
  const seenActivityReplyRef = useRef(new Set());                 // reply msg_ids already counted (dedup optimistic vs WS)
  // Separate from seenActivityReplyRef: dedupes the CHANNEL row's reply_count bump
  // specifically (Thread-panel sends vs. the WS echo of your own reply). Must be its
  // own ref — reusing seenActivityReplyRef here would make the WS handler skip the
  // Activity feed's own count whenever a reply was sent via the Thread panel instead
  // of Activity's inline box, under-counting it there.
  const seenChannelReplyRef = useRef(new Set());
  // Dedupes INSERTING a reply as its own Activity entry. Distinct from
  // seenActivityReplyRef, which the optimistic inline send already consumes for the
  // count bump - sharing it would silently swallow the entry itself.
  const seenActivityItemRef = useRef(new Set());
  // The WS handler closes over stale state, so the active channel filter is mirrored
  // into a ref to decide whether an incoming reply belongs in the current view.
  const activityFilterRef = useRef('');
  useEffect(() => { activityFilterRef.current = activityFilter; }, [activityFilter]);
  const [searchTerm, setSearchTerm] = useState('');

  // Channel state
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  // @mention candidates for the currently-open channel: the channel's own member list
  // (user_ids only) resolved against `users` (which has names) for the autocomplete.
  // Declared early (before any useMentionOverlay(...) calls below) since those hooks
  // synchronously read this on every render once a mention is in progress - defining
  // it later in the component would be a TDZ violation the moment a user types "@".
  const channelMentionCandidates = useMemo(() => {
    if (!selectedChannel?.members?.length || !users.length) return [];
    const memberIds = new Set(selectedChannel.members);
    return users.filter(u => memberIds.has(u.user_id));
  }, [selectedChannel, users]);
  const [channelMessages, setChannelMessages] = useState([]);
  // Channel message pagination (scroll-to-top loads older pages; server-side date filtering)
  const [channelPage, setChannelPage] = useState(1);
  const [channelHasMore, setChannelHasMore] = useState(false);
  const [channelLoadingMore, setChannelLoadingMore] = useState(false);
  // Switching channels refetches a full page; without this the previous channel's
  // messages stay on screen for the whole round trip and it reads as a freeze.
  const [channelLoading, setChannelLoading] = useState(false);
  const [threadLoading, setThreadLoading] = useState(false);
  const channelLoadingMoreRef = useRef(false);  // synchronous re-entrancy guard for rapid scroll events
  // Message edit / in-chat search + filter
  const [editingId, setEditingId] = useState(null);   // message_id (DM) or msg_id (channel)
  const [editText, setEditText] = useState('');
  const [msgSearch, setMsgSearch] = useState('');
  const [msgFilter, setMsgFilter] = useState('all');  // all | media | links
  const [msgDateFrom, setMsgDateFrom] = useState('');  // YYYY-MM-DD (IST)
  const [msgDateTo, setMsgDateTo] = useState('');
  const [activeSection, setActiveSection] = useState('dm');

  // Thread state
  const [threadMsg, setThreadMsg] = useState(null);
  const [threadReplies, setThreadReplies] = useState([]);
  const { text: threadText, setText: setThreadText, clearDraft: clearThreadDraft,
          setMentions: setThreadDraftMentions, getMentions: getThreadDraftMentions } =
    useDraft(threadMsg ? `thread:${threadMsg.msg_id}` : null);
  const [threadFiles, setThreadFiles] = useState([]);
  const [sendingThread, setSendingThread] = useState(false);
  const threadFileInputRef = useRef(null);
  const threadTextareaRef = useRef(null);
  const threadMention = useMentionOverlay(query => filterMentionCandidates(channelMentionCandidates, query));

  // Channel dialog
  const [newChannelDialog, setNewChannelDialog] = useState(false);
  const [channelName, setChannelName] = useState('');
  const [channelDesc, setChannelDesc] = useState('');
  const [channelMembers, setChannelMembers] = useState([]);
  const [creatingChannel, setCreatingChannel] = useState(false);

  // Edit channel dialog
  const [editChannelDialog, setEditChannelDialog] = useState(false);
  const [editChannelName, setEditChannelName] = useState('');
  const [editChannelDesc, setEditChannelDesc] = useState('');
  const [editChannelMembers, setEditChannelMembers] = useState([]);
  const [savingChannel, setSavingChannel] = useState(false);

  // Lightbox
  const [lightboxUrl, setLightboxUrl] = useState(null);

  // Scroll refs
  const dmScrollRef = useRef(null);
  const channelScrollRef = useRef(null);
  const [highlightMsgId, setHighlightMsgId] = useState(null);  // briefly flash a message when its thread is opened
  const threadScrollRef = useRef(null);
  const atDmBottomRef = useRef(true);
  const atChannelBottomRef = useRef(true);

  // Stable refs so WS handlers always see fresh selected state
  const selectedConvRef = useRef(null);
  const selectedChannelRef = useRef(null);
  const threadMsgRef = useRef(null);

  useEffect(() => { selectedConvRef.current = selectedConversation; }, [selectedConversation]);
  useEffect(() => { selectedChannelRef.current = selectedChannel; }, [selectedChannel]);
  useEffect(() => { threadMsgRef.current = threadMsg; }, [threadMsg]);
  // A restored draft brings its mentions back with it, so an "@Sara" typed before
  // switching away still sends as a real mention rather than plain text.
  useEffect(() => { dmMention.loadMentions(getDmDraftMentions());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversationKey]);
  useEffect(() => { threadMention.loadMentions(getThreadDraftMentions());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadMsg?.msg_id]);

  // Send a buzz ("missed-call" ring) to a channel's members or a DM peer
  const sendBuzz = async (scope, id, label) => {
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const url = scope === 'channel'
        ? `${API_URL}/api/channels/${id}/buzz`
        : `${API_URL}/api/messages/${id}/buzz`;
      const r = await fetch(url, { method: 'POST', headers, body: JSON.stringify({}) });
      if (r.ok) toast.success(`📞 Buzzing ${label}…`);
      else if (r.status === 429) toast.error((await r.json().catch(() => ({})))?.detail || 'Please wait before buzzing again');
      else toast.error('Could not send buzz');
    } catch { toast.error('Could not send buzz'); }
  };

  // Deep-link from a buzz "Answer": ?open=channel:<id> or ?open=dm:<peerId>
  const consumedOpenRef = useRef(false);
  // Message a notification click asked us to land on, applied once it has rendered.
  const pendingJumpRef = useRef(null);
  useEffect(() => {
    if (consumedOpenRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const open = params.get('open');
    if (!open) return;
    // `open` is kind:id[:msg_id] — the optional third part is the message a
    // notification click wants us to land on.
    const [kind, id, targetMsgId] = open.split(':');
    const wantThread = params.get('thread') === '1';
    let done = false;
    if (kind === 'channel') {
      const ch = channels.find(c => c.channel_id === id);
      if (ch) { setSelectedChannel(ch); setActiveSection('channels'); setSelectedConversation(null); setThreadMsg(null); done = true; }
    } else if (kind === 'dm') {
      const conv = conversations.find(c => c.user_id === id) || users.find(u => u.user_id === id);
      if (conv) { setSelectedConversation({ user_id: conv.user_id, name: conv.name, email: conv.email, role: conv.role }); setActiveSection('dm'); setSelectedChannel(null); setThreadMsg(null); done = true; }
    }
    if (done) {
      consumedOpenRef.current = true;
      // Messages load right after the channel/DM is selected, so defer the jump
      // until the target row actually exists in the DOM.
      if (targetMsgId && kind === 'channel') {
        pendingJumpRef.current = { msgId: targetMsgId, thread: wantThread, tries: 0 };
      }
      params.delete('open');
      params.delete('thread');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, conversations, users]);

  const { registerHandler, soundEnabled, toggleSound, testNotification, trackThreadParticipation, hasParticipated, resetUnread } = useChatNotification();

  // Clear handler on unmount so context doesn't call into a dead component
  useEffect(() => () => registerHandler(null), [registerHandler]);

  const isAdmin = user?.role === 'admin';
  // The Process button follows the permission the backend actually enforces
  // (transaction_requests:approve), not the raw admin role — otherwise everyone
  // else who is allowed to process withdrawals never sees the button.
  const { canApprove } = usePermissions();
  const navigate = useNavigate();

  // ── Edit / delete message handlers ─────────────────────────────────────────
  // editTextRef mirrors editText so stable (useCallback) handlers below can read the
  // latest value without needing editText in their dependency array — that would
  // otherwise force a new callback identity (and re-render of memoized rows) on
  // every keystroke while editing.
  const editTextRef = useRef('');
  const startEdit = useCallback((id, content) => { setEditingId(id); setEditText(content || ''); editTextRef.current = content || ''; }, []);
  const cancelEdit = useCallback(() => { setEditingId(null); setEditText(''); }, []);
  const onEditTextChange = useCallback((val) => { setEditText(val); editTextRef.current = val; }, []);

  const saveEditDM = async (message_id) => {
    const content = editText.trim();
    try {
      const r = await fetch(`${API_URL}/api/messages/${message_id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content }),
      });
      if (r.ok) {
        const u = await r.json();
        setMessages(prev => prev.map(m => m.message_id === message_id ? u : m));
        cancelEdit();
      } else { toast.error((await r.json().catch(() => ({})))?.detail || 'Edit failed'); }
    } catch { toast.error('Edit failed'); }
  };

  const deleteDM = async (message_id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      const r = await fetch(`${API_URL}/api/messages/${message_id}`, {
        method: 'DELETE', headers: getAuthHeaders(), credentials: 'include',
      });
      if (r.ok) setMessages(prev => prev.map(m => m.message_id === message_id ? { ...m, deleted: true, content: '', attachment: null } : m));
      else toast.error('Delete failed');
    } catch { toast.error('Delete failed'); }
  };

  // Reads editTextRef (not the editText state) so this callback's identity stays stable
  // while the user is typing in the edit box — only depends on selectedChannel, which
  // changes rarely (channel switch), unlike editText which changes every keystroke.
  const saveEditChannel = useCallback(async (msg_id) => {
    const content = editTextRef.current.trim();
    if (!selectedChannel) return;
    try {
      const r = await fetch(`${API_URL}/api/channels/${selectedChannel.channel_id}/messages/${msg_id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content }),
      });
      if (r.ok) {
        const u = await r.json();
        setChannelMessages(prev => prev.map(m => m.msg_id === msg_id ? { ...m, ...u } : m));
        cancelEdit();
      } else { toast.error((await r.json().catch(() => ({})))?.detail || 'Edit failed'); }
    } catch { toast.error('Edit failed'); }
  }, [selectedChannel, getAuthHeaders, cancelEdit]);

  const deleteChannelMsg = useCallback(async (msg_id) => {
    if (!selectedChannel || !window.confirm('Delete this message?')) return;
    try {
      const r = await fetch(`${API_URL}/api/channels/${selectedChannel.channel_id}/messages/${msg_id}`, {
        method: 'DELETE', headers: getAuthHeaders(), credentials: 'include',
      });
      if (r.ok) setChannelMessages(prev => prev.map(m => m.msg_id === msg_id ? { ...m, deleted: true, content: '', attachments: [] } : m));
      else toast.error('Delete failed');
    } catch { toast.error('Delete failed'); }
  }, [selectedChannel, getAuthHeaders]);

  const saveEditReply = async (msg_id) => {
    const content = editText.trim();
    if (!selectedChannel) return;
    try {
      const r = await fetch(`${API_URL}/api/channels/${selectedChannel.channel_id}/messages/${msg_id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content }),
      });
      if (r.ok) {
        const u = await r.json();
        setThreadReplies(prev => prev.map(m => m.msg_id === msg_id ? { ...m, ...u } : m));
        cancelEdit();
      } else { toast.error((await r.json().catch(() => ({})))?.detail || 'Edit failed'); }
    } catch { toast.error('Edit failed'); }
  };

  const deleteReply = async (msg_id) => {
    if (!selectedChannel || !window.confirm('Delete this reply?')) return;
    try {
      const r = await fetch(`${API_URL}/api/channels/${selectedChannel.channel_id}/messages/${msg_id}`, {
        method: 'DELETE', headers: getAuthHeaders(), credentials: 'include',
      });
      if (r.ok) setThreadReplies(prev => prev.map(m => m.msg_id === msg_id ? { ...m, deleted: true, content: '', attachments: [] } : m));
      else toast.error('Delete failed');
    } catch { toast.error('Delete failed'); }
  };

  // In-chat search + filter predicate
  const msgMatches = (m) => {
    if (msgFilter === 'media' && !(m.attachment || (m.attachments && m.attachments.length))) return false;
    if (msgFilter === 'links' && !/https?:\/\//i.test(m.content || '')) return false;
    if (msgSearch && !((m.content || '').toLowerCase().includes(msgSearch.toLowerCase()))) return false;
    if (msgDateFrom || msgDateTo) {
      // Compare on the IST calendar day so it matches the timestamps shown in chat
      const day = m.created_at ? new Date(m.created_at).toLocaleDateString('en-CA', { timeZone: IST }) : '';
      if (msgDateFrom && day < msgDateFrom) return false;
      if (msgDateTo && day > msgDateTo) return false;
    }
    return true;
  };

  // Approve / reject a pending transaction straight from its #deposite_only/#withdraw_only card
  const handleTxComplete = useCallback(async (msg) => {
    const note = window.prompt('Add a completion note (optional):');
    if (note === null) return;
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const r = await fetch(`${API_URL}/api/chat/tx-complete`, {
        method: 'POST', headers,
        body: JSON.stringify({ request_id: msg.tx_request_id, note: note || '' }),
      });
      if (r.ok) toast.success('✅ Marked complete');
      else toast.error((await r.json().catch(() => ({})))?.detail || 'Could not complete');
    } catch { toast.error('Could not complete'); }
  }, [getAuthHeaders]);

  // Toggle an emoji reaction on a message (channel or DM); the server bumps it to the latest.
  const handleReact = useCallback(async (msg, emoji, scope) => {
    try {
      const url = scope === 'channel'
        ? `${API_URL}/api/channels/${selectedChannel?.channel_id}/messages/${msg.msg_id}/react`
        : `${API_URL}/api/messages/${msg.message_id}/react`;
      const r = await fetch(url, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji }),
      });
      if (!r.ok) return;
      const data = await r.json();
      if (scope === 'channel') {
        setChannelMessages(prev => prev.map(m =>
          m.msg_id === msg.msg_id ? { ...m, reactions: data.reactions } : m));
        setThreadReplies(prev => prev.map(m =>
          m.msg_id === msg.msg_id ? { ...m, reactions: data.reactions } : m));
      } else {
        setMessages(prev => prev.map(m =>
          m.message_id === msg.message_id ? { ...m, reactions: data.reactions } : m));
      }
    } catch { /* */ }
  }, [selectedChannel, getAuthHeaders]);

  // Toggle a status tag on a channel message (scalping-check workflow); syncs live to all viewers.
  const handleTag = useCallback(async (msg, tag) => {
    try {
      const r = await fetch(`${API_URL}/api/channels/${selectedChannel?.channel_id}/messages/${msg.msg_id}/tag`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ tag }),
      });
      if (!r.ok) return;
      const data = await r.json();
      setChannelMessages(prev => prev.map(m => m.msg_id === msg.msg_id ? { ...m, tags: data.tags } : m));
      setThreadReplies(prev => prev.map(m => m.msg_id === msg.msg_id ? { ...m, tags: data.tags } : m));
      setThreadMsg(prev => (prev && prev.msg_id === msg.msg_id) ? { ...prev, tags: data.tags } : prev);
    } catch { /* */ }
  }, [selectedChannel, getAuthHeaders]);

  // Scroll the main channel list to a message and briefly flash it (used when opening its thread).
  const scrollToChannelMessage = useCallback((msgId) => {
    setHighlightMsgId(msgId);
    setTimeout(() => {
      document.getElementById(`chmsg-${msgId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    setTimeout(() => setHighlightMsgId(cur => (cur === msgId ? null : cur)), 2400);
  }, []);

  // Open a channel message from elsewhere (e.g. clicking a reply-DM bubble): switch to
  // the channel, scroll to the message and open its thread. Reuses pendingJumpRef so the
  // jump waits for the channel's messages to load.
  const jumpToChannelMessage = (channelId, msgId, thread) => {
    const ch = channels.find(c => c.channel_id === channelId);
    if (!ch) { toast.error('Channel not found'); return; }
    setActiveSection('channels');
    setSelectedConversation(null);
    setThreadMsg(null);
    if (selectedChannel?.channel_id === channelId) {
      // Already on this channel — messages are loaded, so jump straight away.
      scrollToChannelMessage(msgId);
      if (thread) {
        const target = channelMessages.find(m => m.msg_id === msgId);
        if (target) { setThreadMsg(target); fetchThreadReplies(channelId, msgId); }
      }
    } else {
      setSelectedChannel(ch);
      pendingJumpRef.current = { msgId, thread: !!thread, tries: 0 };
    }
  };

  // Land on the message a notification click targeted. The channel's messages arrive
  // asynchronously, so retry briefly until the row exists rather than firing blindly.
  useEffect(() => {
    const jump = pendingJumpRef.current;
    if (!jump || !channelMessages.length) return;
    const target = channelMessages.find(m => m.msg_id === jump.msgId);
    if (!target) {
      // Not in the loaded window (older message) — give up quietly after a few tries.
      jump.tries += 1;
      if (jump.tries > 5) pendingJumpRef.current = null;
      return;
    }
    pendingJumpRef.current = null;
    scrollToChannelMessage(jump.msgId);
    if (jump.thread && selectedChannel) {
      setThreadMsg(target);
      fetchThreadReplies(selectedChannel.channel_id, jump.msgId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelMessages, selectedChannel]);

  // Process a withdrawal request from its #withdraw_only card (captcha-gated, really processes it)
  const handleTxProcess = useCallback(async (msg) => {
    const a = Math.floor(Math.random() * 8) + 2, b = Math.floor(Math.random() * 8) + 2;
    const ans = window.prompt(`Confirm processing — what is ${a} + ${b}?`);
    if (ans === null) return;
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const r = await fetch(`${API_URL}/api/transaction-requests/${msg.tx_request_id}/process`, {
        method: 'POST', headers,
        body: JSON.stringify({ captcha_answer: ans, captcha_expected: String(a + b) }),
      });
      if (r.ok) toast.success('🟢 Request processed');
      else toast.error((await r.json().catch(() => ({})))?.detail || 'Could not process');
    } catch { toast.error('Could not process'); }
  }, [getAuthHeaders]);

  const navigateToTx = useCallback((txReference) => {
    navigate(`/transactions?search=${encodeURIComponent(txReference)}`);
  }, [navigate]);

  // Thin wrapper binding the module-level renderer to this component's lightbox state.
  const renderAttachments = (attachments, isSelf) => renderAttachmentsBase(attachments, isSelf, setLightboxUrl);

  // Scroll helpers
  const scrollToBottom = (ref, instant = false) => {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  const handleDmScroll = () => {
    const el = dmScrollRef.current;
    if (!el) return;
    atDmBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };
  const handleChannelScroll = () => {
    const el = channelScrollRef.current;
    if (!el) return;
    atChannelBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (el.scrollTop < 120 && selectedChannel && channelHasMore && !channelLoadingMoreRef.current) {
      channelLoadingMoreRef.current = true;
      setChannelLoadingMore(true);
      fetchChannelMessages(selectedChannel.channel_id, {
        page: channelPage + 1, dateFrom: msgDateFrom, dateTo: msgDateTo, search: msgSearch, append: true,
      });
    }
  };

  // ── Register WS handler with global context (reassigned each render for fresh closure) ──
  registerHandler((data) => {
    switch (data.type) {
      case 'dm_message': {
        const msg = data.message;
        const otherId = msg.sender_id === user?.user_id ? msg.recipient_id : msg.sender_id;
        if (selectedConvRef.current?.user_id === otherId) {
          setMessages(prev => prev.some(m => m.message_id === msg.message_id) ? prev : [...prev, msg]);
        } else if (msg.sender_id !== user?.user_id) {
          // In-page toast when on Messages page but viewing a different conversation.
          // A mention gets a distinct, longer-lived notification so it cuts through.
          const preview = msg.content || (msg.attachments?.length ? '📎 Attachment' : '');
          const isMentioned = msg.mentions?.some(m => m.user_id === user?.user_id);
          toast(isMentioned ? `🔔 ${msg.sender_name} mentioned you` : `💬 ${msg.sender_name}`, {
            description: preview,
            duration: isMentioned ? 10000 : 6000,
            action: {
              label: 'View',
              onClick: () => {
                const conv = conversations.find(c => c.user_id === otherId)
                  || { user_id: otherId, name: msg.sender_name, email: '', role: '' };
                setSelectedConversation(conv);
                setActiveSection('dm');
                setSelectedChannel(null);
                setThreadMsg(null);
              },
            },
          });
        }
        setConversations(prev => {
          const exists = prev.some(c => c.user_id === otherId);
          const preview = msg.content || (msg.attachments?.length ? '📎 Attachment' : '');
          if (exists) {
            return prev.map(c => c.user_id === otherId ? {
              ...c,
              last_message: preview,
              unread_count: selectedConvRef.current?.user_id === otherId ? 0
                : (c.unread_count || 0) + (msg.sender_id === user?.user_id ? 0 : 1),
            } : c);
          }
          if (msg.sender_id !== user?.user_id) {
            return [{ user_id: msg.sender_id, name: msg.sender_name, email: '', role: '', last_message: preview, unread_count: 1 }, ...prev];
          }
          return prev;
        });
        break;
      }
      case 'dm_read': {
        if (selectedConvRef.current?.user_id === data.reader_id) {
          setMessages(prev => prev.map(m => ({ ...m, read: true })));
        }
        break;
      }
      case 'channel_message': {
        const msg = data.message;
        if (selectedChannelRef.current?.channel_id === msg.channel_id) {
          setChannelMessages(prev => prev.some(m => m.msg_id === msg.msg_id) ? prev : [...prev, msg]);
        } else if (msg.sender_id !== user?.user_id) {
          // In-page toast for a different channel. A mention gets a distinct,
          // longer-lived notification so it cuts through.
          const chName = data.channel_name || channels.find(c => c.channel_id === msg.channel_id)?.name || 'Channel';
          const isMentioned = msg.mentions?.some(m => m.user_id === user?.user_id);
          toast(isMentioned ? `🔔 ${msg.sender_name} mentioned you in #${chName}` : `# ${chName}`, {
            description: `${msg.sender_name}: ${msg.content || '📎'}`,
            duration: isMentioned ? 10000 : 6000,
            action: {
              label: 'View',
              onClick: () => {
                const ch = channels.find(c => c.channel_id === msg.channel_id);
                if (ch) { setSelectedChannel(ch); setActiveSection('channels'); setSelectedConversation(null); setThreadMsg(null); }
              },
            },
          });
        }
        setChannels(prev => prev.map(ch => ch.channel_id === msg.channel_id ? {
          ...ch,
          last_message: msg.content || '📎 Media',
          unread_count: selectedChannelRef.current?.channel_id === msg.channel_id
            ? ch.unread_count
            : (ch.unread_count || 0) + (msg.sender_id === user?.user_id ? 0 : 1),
        } : ch));
        break;
      }
      case 'thread_reply': {
        const reply = data.message;
        if (threadMsgRef.current?.msg_id === reply.thread_root_id) {
          setThreadReplies(prev => prev.some(m => m.msg_id === reply.msg_id) ? prev : [...prev, reply]);
        }
        // Dedup against seenChannelReplyRef (not a channelMessages lookup — replies never
        // appear in that top-level list, so that check could never match and silently
        // double-counted every reply you sent yourself: once optimistically in
        // handleSendThreadReply, and again here when the WS echoed your own broadcast back).
        if (selectedChannelRef.current?.channel_id === reply.channel_id && !seenChannelReplyRef.current.has(reply.msg_id)) {
          seenChannelReplyRef.current.add(reply.msg_id);
          setChannelMessages(prev => prev.map(m => m.msg_id === reply.thread_root_id
            ? { ...m, reply_count: (m.reply_count || 0) + 1 }
            : m));
        }
        // Keep the Activity feed's reply_count live (dedup against the optimistic bump)
        if (!seenActivityReplyRef.current.has(reply.msg_id)) {
          seenActivityReplyRef.current.add(reply.msg_id);
          setActivityItems(prev => prev.some(m => m.msg_id === reply.thread_root_id)
            ? prev.map(m => m.msg_id === reply.thread_root_id ? { ...m, reply_count: (m.reply_count || 0) + 1 } : m)
            : prev);
        }
        // The reply itself also lands in Activity as its own entry, carrying the
        // message it answers. Guarded separately from the count bump above: that one
        // is consumed by the optimistic send, so reusing its ref would drop the entry
        // for replies sent from this tab's own inline box.
        if (!seenActivityItemRef.current.has(reply.msg_id)) {
          seenActivityItemRef.current.add(reply.msg_id);
          setActivityItems(prev => {
            if (prev.some(m => m.msg_id === reply.msg_id)) return prev;
            // Only channels already represented in the feed are in view; a reply from
            // a channel the current filter excludes must not jump in.
            const parent = prev.find(m => m.msg_id === reply.thread_root_id);
            if (activityFilterRef.current && activityFilterRef.current !== reply.channel_id) return prev;
            return [{
              ...reply,
              is_reply: true,
              channel_name: parent?.channel_name
                || activityChannels.find(c => c.channel_id === reply.channel_id)?.name
                || '',
              parent_sender_name: data.parent_sender_name || parent?.sender_name,
              parent_content: data.parent_content ?? parent?.content ?? '',
            }, ...prev];
          });
        }
        // In-page toast if user is author, participated, or was mentioned in this
        // thread but isn't currently viewing it. A mention surfaces even if you
        // weren't otherwise part of the thread, with a distinct, longer-lived toast.
        if (reply.sender_id !== user?.user_id) {
          const isParent = data.parent_sender_id === user?.user_id;
          const participated = hasParticipated(reply.channel_id, reply.thread_root_id);
          const isMentioned = reply.mentions?.some(m => m.user_id === user?.user_id);
          const threadOpen = threadMsgRef.current?.msg_id === reply.thread_root_id;
          if ((isParent || participated || isMentioned) && !threadOpen) {
            toast(isMentioned ? `🔔 ${reply.sender_name} mentioned you in a thread` : `↩ ${reply.sender_name} replied in thread`, {
              description: reply.content || '📎',
              duration: isMentioned ? 10000 : 6000,
            });
          }
        }
        break;
      }
      case 'dm_edit':
      case 'dm_delete': {
        const m = data.message;
        setMessages(prev => prev.map(x => x.message_id === m.message_id ? m : x));
        break;
      }
      case 'channel_edit':
      case 'channel_delete': {
        const m = data.message;
        setChannelMessages(prev => prev.map(x => x.msg_id === m.msg_id ? { ...x, ...m } : x));
        setThreadReplies(prev => prev.map(x => x.msg_id === m.msg_id ? { ...x, ...m } : x));
        break;
      }
      case 'reaction': {
        if (data.scope === 'channel') {
          setChannelMessages(prev => prev.map(x => x.msg_id === data.msg_id
            ? { ...x, reactions: data.reactions } : x));
          setThreadReplies(prev => prev.map(x => x.msg_id === data.msg_id
            ? { ...x, reactions: data.reactions } : x));
        } else {
          setMessages(prev => prev.map(x => x.message_id === data.message_id
            ? { ...x, reactions: data.reactions } : x));
        }
        break;
      }
      case 'tag': {
        setChannelMessages(prev => prev.map(x => x.msg_id === data.msg_id
          ? { ...x, tags: data.tags } : x));
        setThreadReplies(prev => prev.map(x => x.msg_id === data.msg_id
          ? { ...x, tags: data.tags } : x));
        setThreadMsg(prev => (prev && prev.msg_id === data.msg_id)
          ? { ...prev, tags: data.tags } : prev);
        break;
      }
      default: break;
    }
  });

  // Auto-scroll when new messages arrive
  useEffect(() => {
    if (atDmBottomRef.current) scrollToBottom(dmScrollRef);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    if (atChannelBottomRef.current) scrollToBottom(channelScrollRef);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelMessages]);

  useEffect(() => {
    scrollToBottom(threadScrollRef);
  }, [threadReplies]);

  // ── Fetchers ────────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    try { const r = await fetch(`${API_URL}/api/messages/users`, { headers: getAuthHeaders() }); if (r.ok) setUsers(await r.json()); } catch { /* */ }
  }, [getAuthHeaders]);

  const fetchConversations = useCallback(async () => {
    try { const r = await fetch(`${API_URL}/api/messages/conversations`, { headers: getAuthHeaders() }); if (r.ok) setConversations(await r.json()); } catch { /* */ }
  }, [getAuthHeaders]);

  const fetchMessages = useCallback(async (recipientId) => {
    if (!recipientId) return;
    try {
      const r = await fetch(`${API_URL}/api/messages/conversation/${recipientId}`, { headers: getAuthHeaders() });
      if (r.ok) { setMessages(await r.json()); atDmBottomRef.current = true; setTimeout(() => scrollToBottom(dmScrollRef), 60); }
    } catch { /* */ }
  }, [getAuthHeaders]);

  const fetchAllConversations = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingAll(true);
    try { const r = await fetch(`${API_URL}/api/messages/admin/all-conversations`, { headers: getAuthHeaders() }); if (r.ok) setAllConversations(await r.json()); } catch { /* */ } finally { setLoadingAll(false); }
  }, [getAuthHeaders, isAdmin]);

  const fetchConversationMessages = useCallback(async (u1, u2) => {
    if (!isAdmin) return;
    try { const r = await fetch(`${API_URL}/api/messages/admin/conversation/${u1}/${u2}`, { headers: getAuthHeaders() }); if (r.ok) setAllMessages(await r.json()); } catch { /* */ }
  }, [getAuthHeaders, isAdmin]);

  const fetchActivity = useCallback(async (page = 1, channelId = '') => {
    setActivityLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(page), page_size: '40' });
      if (channelId) qs.set('channel_id', channelId);
      const r = await fetch(`${API_URL}/api/channels/activity?${qs.toString()}`, { headers: getAuthHeaders() });
      if (r.ok) {
        const d = await r.json();
        setActivityItems(prev => (page > 1 ? [...prev, ...(d.items || [])] : (d.items || [])));
        setActivityChannels(d.channels || []);
        setActivityHasMore((d.page || 1) < (d.total_pages || 1));
        setActivityPage(d.page || 1);
      }
    } catch { /* */ } finally { setActivityLoading(false); }
  }, [getAuthHeaders]);

  const markAsRead = useCallback(async (recipientId) => {
    try { await fetch(`${API_URL}/api/messages/mark-read/${recipientId}`, { method: 'PUT', headers: getAuthHeaders() }); } catch { /* */ }
  }, [getAuthHeaders]);

  const fetchChannels = useCallback(async () => {
    try { const r = await fetch(`${API_URL}/api/channels`, { headers: getAuthHeaders() }); if (r.ok) setChannels(await r.json()); } catch { /* */ }
  }, [getAuthHeaders]);

  // Fetches one page of channel messages (server-side paginated, newest-first window
  // reversed to ascending). Pass { page, dateFrom, dateTo } to page back / filter by IST
  // calendar day, and { append: true } to prepend an older page onto the currently
  // loaded messages (used by scroll-to-top infinite loading) while preserving scroll
  // position, instead of replacing the whole list.
  const fetchChannelMessages = useCallback(async (channelId, opts = {}) => {
    if (!channelId) return;
    const { page = 1, dateFrom = '', dateTo = '', search = '', append = false } = opts;
    if (!append) setChannelLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(page), page_size: '50' });
      if (dateFrom) qs.set('date_from', dateFrom);
      if (dateTo) qs.set('date_to', dateTo);
      if (search) qs.set('search', search);
      const r = await fetch(`${API_URL}/api/channels/${channelId}/messages?${qs.toString()}`, { headers: getAuthHeaders() });
      if (r.ok) {
        const d = await r.json();
        const items = d.items || [];
        setChannelHasMore(!!d.has_more);
        setChannelPage(d.page || page);
        if (append) {
          const el = channelScrollRef.current;
          // Keep the reader where they were by pinning a real message element rather
          // than doing pixel arithmetic on scrollHeight. These cards carry receipt
          // images that finish loading AFTER the first frame, so a one-shot
          // "scrollTop += heightDelta" is measured before the content above has
          // settled and leaves the view drifted by a screenful or more.
          //
          // The anchor is the topmost rendered message; we record how far below the
          // container's top edge it sits, then restore exactly that offset - once on
          // the next frame, and again as the prepended content changes size.
          const anchorEl = el ? el.querySelector('[id^="chmsg-"]') : null;
          const anchorId = anchorEl ? anchorEl.id : null;
          const offsetWas = anchorEl && el
            ? anchorEl.getBoundingClientRect().top - el.getBoundingClientRect().top
            : 0;
          setChannelMessages(prev => [...items, ...prev]);
          if (el && anchorId) {
            const repin = () => {
              const now = document.getElementById(anchorId);
              if (!now) return;
              const delta =
                (now.getBoundingClientRect().top - el.getBoundingClientRect().top) - offsetWas;
              if (Math.abs(delta) > 1) el.scrollTop += delta;
            };
            requestAnimationFrame(repin);
            // Images above resolve over the next moment; re-pin while that happens,
            // then stop. ResizeObserver catches the growth without polling.
            if (typeof ResizeObserver !== 'undefined') {
              const ro = new ResizeObserver(repin);
              Array.from(el.children).forEach(c => ro.observe(c));
              setTimeout(() => ro.disconnect(), 3000);
            }
          }
        } else {
          setChannelMessages(items);
          atChannelBottomRef.current = true;
          setTimeout(() => scrollToBottom(channelScrollRef), 60);
        }
      }
    } catch { /* */ } finally {
      if (append) { channelLoadingMoreRef.current = false; setChannelLoadingMore(false); }
      else setChannelLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchThreadReplies = useCallback(async (channelId, msgId) => {
    if (!channelId || !msgId) return;
    setThreadLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/channels/${channelId}/messages/${msgId}/replies`, { headers: getAuthHeaders() });
      if (r.ok) { setThreadReplies(await r.json()); setTimeout(() => scrollToBottom(threadScrollRef), 60); }
    } catch { /* */ } finally { setThreadLoading(false); }
  }, [getAuthHeaders]);

  // Stable per-message "open thread" handler for the memoized channel row list — takes
  // msg as a call-time argument (from the row's own props) rather than closing over it,
  // so this single function reference works for every row without ever going stale.
  const openThreadForMsg = useCallback((msg) => {
    setThreadMsg(msg);
    if (selectedChannel) fetchThreadReplies(selectedChannel.channel_id, msg.msg_id);
    scrollToChannelMessage(msg.msg_id);
  }, [selectedChannel, fetchThreadReplies, scrollToChannelMessage]);

  const markChannelRead = useCallback(async (channelId) => {
    try {
      await fetch(`${API_URL}/api/channels/${channelId}/mark-read`, { method: 'PUT', headers: getAuthHeaders() });
      setChannels(prev => prev.map(ch => ch.channel_id === channelId ? { ...ch, unread_count: 0 } : ch));
    } catch { /* */ }
  }, [getAuthHeaders]);

  // ── Send handlers ──────────────────────────────────────────────────────────
  // Keeps the DM draft's text and mentions in sync - see updateMsg in ChannelComposer.
  const updateNewMessage = (next) => {
    setDmDraftMentions(dmMention.getMentionsForSend());
    setNewMessage(next);
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !attachment) || !selectedConversation) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('recipient_id', selectedConversation.user_id);
      fd.append('content', newMessage);
      fd.append('mentions', JSON.stringify(dmMention.getMentionsForSend()));
      if (attachment) fd.append('attachment', attachment);
      const headers = { ...getAuthHeaders() }; delete headers['Content-Type'];
      const r = await fetch(`${API_URL}/api/messages/send`, { method: 'POST', headers, body: fd });
      if (r.ok) {
        const msg = await r.json();
        clearDmDraft(); setAttachment(null);
        dmMention.resetMentions();
        setMessages(prev => prev.some(m => m.message_id === msg.message_id) ? prev : [...prev, msg]);
        setConversations(prev => prev.map(c => c.user_id === selectedConversation.user_id
          ? { ...c, last_message: msg.content || '📎 Attachment' } : c));
      } else toast.error(await getApiError(r));
    } catch (e) { toast.error(e?.message || 'Something went wrong'); } finally { setSending(false); }
  };

  // Called by ChannelComposer once a message has actually been sent (the composer
  // does the fetch itself, keeping its own draft state fully isolated).
  const handleChannelMessageSent = useCallback((msg) => {
    setChannelMessages(prev => prev.some(m => m.msg_id === msg.msg_id) ? prev : [...prev, msg]);
    setChannels(prev => prev.map(ch => ch.channel_id === msg.channel_id
      ? { ...ch, last_message: msg.content || '📎 Media', last_message_at: msg.created_at } : ch));
  }, []);

  // Keeps the thread draft's text and mentions in sync - see updateMsg in ChannelComposer.
  const updateThreadText = (next) => {
    setThreadDraftMentions(threadMention.getMentionsForSend());
    setThreadText(next);
  };

  const handleSendThreadReply = async () => {
    if ((!threadText.trim() && !threadFiles.length) || !threadMsg) return;
    setSendingThread(true);
    try {
      const fd = new FormData();
      fd.append('content', threadText);
      fd.append('mentions', JSON.stringify(threadMention.getMentionsForSend()));
      threadFiles.forEach(f => fd.append('files', f));
      const headers = { ...getAuthHeaders() }; delete headers['Content-Type'];
      const r = await fetch(`${API_URL}/api/channels/${selectedChannel.channel_id}/messages/${threadMsg.msg_id}/replies`, { method: 'POST', headers, body: fd });
      if (r.ok) {
        const reply = await r.json();
        clearThreadDraft(); setThreadFiles([]);
        threadMention.resetMentions();
        if (threadTextareaRef.current) threadTextareaRef.current.style.height = 'auto';
        setThreadReplies(prev => prev.some(m => m.msg_id === reply.msg_id) ? prev : [...prev, reply]);
        // Mark seen BEFORE the WS echo of this same reply arrives, so the thread_reply
        // handler's channelMessages bump (above) skips it instead of double-counting.
        if (reply?.msg_id) seenChannelReplyRef.current.add(reply.msg_id);
        setChannelMessages(prev => prev.map(m => m.msg_id === threadMsg.msg_id
          ? { ...m, reply_count: (m.reply_count || 0) + 1 } : m));
        trackThreadParticipation(selectedChannel.channel_id, threadMsg.msg_id);
      } else toast.error(await getApiError(r));
    } catch (e) { toast.error(e?.message || 'Something went wrong'); } finally { setSendingThread(false); }
  };

  // Inline reply from the Activity feed — posts to the message's thread without
  // leaving the tab. Optimistically bumps reply_count and marks the reply id as
  // seen so the echoed WS thread_reply event doesn't double-count it.
  const handleActivityReply = async (msg) => {
    const text = activityReplyText.trim();
    if (!text || activityReplySending) return;
    // Replying to a reply continues the same thread rather than starting a nested
    // one - threads are one level deep, so the root is always the target.
    const rootId = msg.thread_root_id || msg.msg_id;
    setActivityReplySending(true);
    try {
      const fd = new FormData();
      fd.append('content', text);
      const headers = { ...getAuthHeaders() }; delete headers['Content-Type'];
      const r = await fetch(`${API_URL}/api/channels/${msg.channel_id}/messages/${rootId}/replies`, { method: 'POST', headers, body: fd });
      if (r.ok) {
        const reply = await r.json();
        if (reply?.msg_id) seenActivityReplyRef.current.add(reply.msg_id);
        setActivityItems(prev => prev.map(m => m.msg_id === rootId ? { ...m, reply_count: (m.reply_count || 0) + 1 } : m));
        trackThreadParticipation(msg.channel_id, rootId);
        setActivityReplyText(''); setActivityReplyId(null);
        toast.success('Reply sent');
      } else toast.error(await getApiError(r));
    } catch (e) { toast.error(e?.message || 'Something went wrong'); } finally { setActivityReplySending(false); }
  };

  const handleCreateChannel = async () => {
    if (!channelName.trim()) { toast.error('Channel name is required'); return; }
    setCreatingChannel(true);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const r = await fetch(`${API_URL}/api/channels`, { method: 'POST', headers, body: JSON.stringify({ name: channelName.trim(), description: channelDesc, members: channelMembers }) });
      if (r.ok) {
        const ch = await r.json();
        setNewChannelDialog(false); setChannelName(''); setChannelDesc(''); setChannelMembers([]);
        await fetchChannels();
        setSelectedChannel(ch); setActiveSection('channels');
      } else toast.error(await getApiError(r));
    } catch (e) { toast.error(e?.message || 'Something went wrong'); } finally { setCreatingChannel(false); }
  };

  const openEditDialog = () => {
    if (!selectedChannel) return;
    setEditChannelName(selectedChannel.name || '');
    setEditChannelDesc(selectedChannel.description || '');
    setEditChannelMembers(selectedChannel.members || []);
    setEditChannelDialog(true);
  };

  const handleEditChannel = async () => {
    if (!editChannelName.trim()) { toast.error('Channel name is required'); return; }
    setSavingChannel(true);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const r = await fetch(`${API_URL}/api/channels/${selectedChannel.channel_id}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ name: editChannelName.trim(), description: editChannelDesc, members: editChannelMembers }),
      });
      if (r.ok) {
        const updated = await r.json();
        setEditChannelDialog(false);
        setSelectedChannel(updated);
        setChannels(prev => prev.map(ch => ch.channel_id === updated.channel_id ? { ...ch, ...updated } : ch));
        toast.success('Channel updated');
      } else toast.error(await getApiError(r));
    } catch (e) { toast.error(e?.message || 'Something went wrong'); } finally { setSavingChannel(false); }
  };

  const handleDeleteChannel = async () => {
    if (!selectedChannel) return;
    if (!window.confirm(`Delete #${selectedChannel.name}? This cannot be undone.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/channels/${selectedChannel.channel_id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (r.ok) {
        setEditChannelDialog(false);
        setSelectedChannel(null);
        setChannelMessages([]);
        setActiveSection('dm');
        setChannels(prev => prev.filter(ch => ch.channel_id !== selectedChannel.channel_id));
        toast.success('Channel deleted');
      } else toast.error(await getApiError(r));
    } catch (e) { toast.error(e?.message || 'Something went wrong'); }
  };

  // ── File handlers ──────────────────────────────────────────────────────────
  const handleDmFileSelect = (e) => {
    const f = e.target.files[0]; if (!f) return;
    if (f.size > 10485760) { toast.error('File size exceeds 10MB limit'); return; }
    setAttachment(f); e.target.value = '';
  };
  const handleThreadFileSelect = (e) => {
    const files = Array.from(e.target.files || []).filter(validateChannelFile);
    setThreadFiles(prev => [...prev, ...files]); e.target.value = '';
  };
  const handleThreadPaste = useCallback((e) => {
    const items = e.clipboardData?.items || []; const pasted = [];
    for (const item of items) { if (item.kind === 'file') { const f = item.getAsFile(); if (f && validateChannelFile(f)) pasted.push(f); } }
    if (pasted.length) setThreadFiles(prev => [...prev, ...pasted]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartNewChat = () => {
    if (!selectedRecipient) { toast.error('Please select a user'); return; }
    const u = users.find(u => u.user_id === selectedRecipient);
    if (u) {
      setSelectedConversation({ user_id: u.user_id, name: u.name, email: u.email, role: u.role });
      setActiveSection('dm'); setSelectedChannel(null); setThreadMsg(null);
      setNewChatDialog(false); setSelectedRecipient('');
    }
  };

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchConversations(), fetchChannels()]);
      setLoading(false);
    };
    init();
  }, [fetchUsers, fetchConversations, fetchChannels]);

  useEffect(() => { if (viewMode === 'all' && isAdmin) fetchAllConversations(); }, [viewMode, isAdmin, fetchAllConversations]);
  useEffect(() => { if (viewMode === 'activity') fetchActivity(1, activityFilter); }, [viewMode, activityFilter, fetchActivity]);
  useEffect(() => { if (selectedAllConversation && isAdmin) fetchConversationMessages(selectedAllConversation.user1_id, selectedAllConversation.user2_id); }, [selectedAllConversation, isAdmin, fetchConversationMessages]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.user_id);
      markAsRead(selectedConversation.user_id);
      setConversations(prev => prev.map(c => c.user_id === selectedConversation.user_id ? { ...c, unread_count: 0 } : c));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation, fetchMessages]);

  useEffect(() => {
    setThreadMsg(null); setThreadReplies([]);
    if (selectedChannel) {
      setChannelPage(1); setChannelHasMore(false);
      fetchChannelMessages(selectedChannel.channel_id, { page: 1, dateFrom: msgDateFrom, dateTo: msgDateTo, search: msgSearch });
      markChannelRead(selectedChannel.channel_id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannel, fetchChannelMessages, markChannelRead]);

  // Re-fetch (server-side) when the date filter or search term changes while a channel is
  // open — kept separate from the channel-switch effect above so it doesn't reset the open
  // thread or re-mark the channel read on every filter tweak. Debounced so search doesn't
  // fire a request on every keystroke; the date inputs only ever change a few times a
  // session, so the same short debounce on them is unnoticeable.
  useEffect(() => {
    if (!selectedChannel) return;
    const handle = setTimeout(() => {
      setChannelPage(1); setChannelHasMore(false);
      fetchChannelMessages(selectedChannel.channel_id, { page: 1, dateFrom: msgDateFrom, dateTo: msgDateTo, search: msgSearch });
    }, 350);
    return () => clearTimeout(handle);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgDateFrom, msgDateTo, msgSearch]);

  useEffect(() => {
    if (threadMsg && selectedChannel) fetchThreadReplies(selectedChannel.channel_id, threadMsg.msg_id);
  }, [threadMsg, selectedChannel, fetchThreadReplies]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const filteredConversations = conversations.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) || c.email?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredAllConversations = allConversations.filter(c =>
    c.user1_name?.toLowerCase().includes(searchTerm.toLowerCase()) || c.user2_name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredChannels = channels.filter(c => c.name?.toLowerCase().includes(searchTerm.toLowerCase()));

  const canProcessTx = canApprove('transaction_requests');

  // Memoized: previously recomputed (map + filter over every loaded message) on EVERY
  // render, including every keystroke anywhere else in this component — with hundreds
  // of messages loaded that was the dominant cost behind the typing/click lag. Now only
  // recomputes when the messages or the active filters actually change.
  const groupedChannelMessages = useMemo(() => {
    const grouped = channelMessages.map((msg, i) => {
      const prev = channelMessages[i - 1];
      const sameDay = prev && new Date(msg.created_at).toDateString() === new Date(prev.created_at).toDateString();
      const isGrouped = prev && prev.sender_id === msg.sender_id && sameDay
        && new Date(msg.created_at) - new Date(prev.created_at) < 300000;
      return { ...msg, isGrouped: !!isGrouped, showDateDivider: !sameDay };
    });
    return grouped.filter(msgMatches);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelMessages, msgFilter, msgSearch, msgDateFrom, msgDateTo]);

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  // ── JSX ────────────────────────────────────────────────────────────────────
  return (
    <div className={fullscreen ? "h-screen flex flex-col p-3 bg-slate-50" : "h-[calc(100vh-120px)] flex flex-col"}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Messages</h1>
          <p className="text-slate-500 mt-0.5 text-sm">Direct messages &amp; group channels</p>
        </div>
        <div className="flex items-center gap-2">
          {!fullscreen && (
            <button
              onClick={() => window.open('/messages/full', '_blank')}
              title="Open full-screen in a new tab"
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={testNotification}
            title="Test notification (sound + browser alert)"
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          >
            <BellRing className="w-4 h-4" /> Test
          </button>
          <button
            onClick={toggleSound}
            title={soundEnabled ? 'Mute notifications' : 'Unmute notifications'}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          >
            {soundEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
          </button>
        <div className="flex items-center bg-slate-100 rounded-lg p-1">
          <Button variant={viewMode === 'my' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('my')} className="rounded-md">
            <User className="w-4 h-4 mr-1" /> My Messages
          </Button>
          <Button variant={viewMode === 'activity' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('activity')} className="rounded-md">
            <Activity className="w-4 h-4 mr-1" /> Activity
          </Button>
          {isAdmin && (
            <Button variant={viewMode === 'all' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('all')} className="rounded-md">
              <Users className="w-4 h-4 mr-1" /> All Communications
            </Button>
          )}
        </div>
        </div>
      </div>

      <NotifyBanner onEnable={testNotification} />

      {/* Admin all-comms view */}
      {isAdmin && viewMode === 'all' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1 min-h-0">
          <Card className="md:col-span-1 flex flex-col">
            <CardHeader className="pb-2 border-b">
              <CardTitle className="text-sm font-medium flex items-center gap-2"><Users className="w-4 h-4" /> All Conversations</CardTitle>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search users..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" />
              </div>
            </CardHeader>
            <CardContent className="flex-1 p-0 overflow-hidden">
              <ScrollArea className="h-full">
                {loadingAll ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
                ) : filteredAllConversations.length === 0 ? (
                  <div className="text-center py-12 text-slate-400"><MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" /><p>No conversations</p></div>
                ) : filteredAllConversations.map((conv, idx) => (
                  <div key={idx} onClick={() => setSelectedAllConversation(conv)}
                    className={`p-4 border-b cursor-pointer hover:bg-slate-50 transition-colors ${selectedAllConversation?.user1_id === conv.user1_id && selectedAllConversation?.user2_id === conv.user2_id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}>
                    <div className="flex items-center gap-3">
                      <div className="flex -space-x-2">
                        <Avatar className="w-8 h-8 border-2 border-white"><AvatarFallback className="bg-blue-100 text-blue-700 text-xs">{getInitials(conv.user1_name)}</AvatarFallback></Avatar>
                        <Avatar className="w-8 h-8 border-2 border-white"><AvatarFallback className="bg-purple-100 text-purple-700 text-xs">{getInitials(conv.user2_name)}</AvatarFallback></Avatar>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{conv.user1_name} &amp; {conv.user2_name}</p>
                        <p className="text-xs text-slate-500">{conv.message_count} msgs · {formatDate(conv.last_message_at)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </ScrollArea>
            </CardContent>
          </Card>
          <Card className="md:col-span-2 flex flex-col">
            <CardHeader className="border-b py-3">
              {selectedAllConversation ? (
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2">
                    <Avatar className="w-10 h-10 border-2 border-white"><AvatarFallback className="bg-blue-100 text-blue-700">{getInitials(selectedAllConversation.user1_name)}</AvatarFallback></Avatar>
                    <Avatar className="w-10 h-10 border-2 border-white"><AvatarFallback className="bg-purple-100 text-purple-700">{getInitials(selectedAllConversation.user2_name)}</AvatarFallback></Avatar>
                  </div>
                  <div>
                    <CardTitle className="text-base">{selectedAllConversation.user1_name} &amp; {selectedAllConversation.user2_name}</CardTitle>
                    <p className="text-xs text-slate-500">Admin view – read only</p>
                  </div>
                </div>
              ) : <CardTitle className="text-slate-400">Select a conversation</CardTitle>}
            </CardHeader>
            <CardContent className="flex-1 p-0 overflow-hidden">
              {selectedAllConversation ? (
                <ScrollArea className="h-full p-4">
                  <div className="space-y-3">
                    {allMessages.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.sender_id === selectedAllConversation.user1_id ? 'justify-start' : 'justify-end'}`}>
                        <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${msg.sender_id === selectedAllConversation.user1_id ? 'bg-slate-100' : 'bg-purple-100'}`}>
                          <p className="text-xs font-medium text-slate-600 mb-0.5">{msg.sender_name}</p>
                          {msg.content && <p className="text-sm text-slate-800 whitespace-pre-wrap">{renderMentionedContent(msg.content, msg.mentions)}</p>}
                          {msg.attachment && (
                            isImage(msg.attachment.filename, msg.attachment.content_type) ? (
                              <div className="relative group mt-1 inline-block cursor-pointer rounded-lg overflow-hidden" onClick={() => setLightboxUrl(msg.attachment.url || `${API_URL}/api/messages/attachment/${msg.message_id}`)}>
                                <img src={msg.attachment.url || `${API_URL}/api/messages/attachment/${msg.message_id}`}
                                  alt={msg.attachment.filename} className="rounded-lg max-h-48 object-cover" style={{ maxWidth: 240 }} />
                                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                  <ZoomIn className="w-6 h-6 text-white drop-shadow" />
                                </div>
                              </div>
                            ) : (
                              <a href="#" onClick={async e => {
                                e.preventDefault();
                                const token = localStorage.getItem('auth_token');
                                const r = await fetch(`${API_URL}/api/messages/attachment/${msg.message_id}`, { headers: { Authorization: `Bearer ${token}` } });
                                const blob = await r.blob(); const url = URL.createObjectURL(blob);
                                const a = document.createElement('a'); a.href = url; a.download = msg.attachment.filename; a.click(); URL.revokeObjectURL(url);
                              }} className="flex items-center gap-2 mt-1 p-2 rounded-lg cursor-pointer bg-white/60 hover:bg-white">
                                {getFileIcon(msg.attachment.filename, msg.attachment.content_type)}
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-medium truncate">{msg.attachment.filename}</p>
                                  {msg.attachment.size && <p className="text-xs text-slate-400">{formatFileSize(msg.attachment.size)}</p>}
                                </div>
                              </a>
                            )
                          )}
                          <span className="text-xs text-slate-400">{formatDate(msg.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  <div className="text-center"><Users className="w-16 h-16 mx-auto mb-4 opacity-30" /><p>Select a conversation</p></div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : viewMode === 'activity' ? (

        /* ── Activity: unified feed of every channel ─────────────────────────── */
        <Card className="flex-1 flex flex-col min-h-0">
          <CardHeader className="border-b py-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4" /> Activity <span className="text-xs font-normal text-slate-400">· all channels, newest first</span></CardTitle>
              <Button variant="ghost" size="sm" onClick={() => fetchActivity(1, activityFilter)} className="text-slate-500">
                <Loader2 className={`w-4 h-4 mr-1 ${activityLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              <button onClick={() => setActivityFilter('')} className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${!activityFilter ? 'bg-blue-600 text-white border-blue-600' : 'text-slate-500 border-slate-200 hover:bg-slate-100'}`}>All</button>
              {activityChannels.map(c => (
                <button key={c.channel_id} onClick={() => setActivityFilter(c.channel_id)}
                  className={`inline-flex items-center gap-0.5 text-xs px-2 py-0.5 rounded-full border transition-colors ${activityFilter === c.channel_id ? 'bg-blue-600 text-white border-blue-600' : 'text-slate-500 border-slate-200 hover:bg-slate-100'}`}>
                  <Hash className="w-2.5 h-2.5" />{c.name}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="flex-1 p-0 overflow-hidden">
            <ScrollArea className="h-full">
              {activityLoading && activityItems.length === 0 ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
              ) : activityItems.length === 0 ? (
                <div className="text-center py-12 text-slate-400"><Activity className="w-12 h-12 mx-auto mb-2 opacity-40" /><p>No activity yet</p></div>
              ) : (
                <div className="divide-y">
                  {activityItems.map(msg => (
                    <div key={msg.msg_id} onClick={() => { setViewMode('my'); jumpToChannelMessage(msg.channel_id, msg.is_reply ? msg.thread_root_id : msg.msg_id, false); }}
                      className="flex gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer transition-colors">
                      <Avatar className="w-8 h-8 shrink-0">
                        <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-xs">{getInitials(msg.sender_name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-blue-700 bg-blue-50 rounded px-1.5 py-0.5"><Hash className="w-2.5 h-2.5" />{msg.channel_name}</span>
                          <span className="text-sm font-bold text-slate-900">{msg.sender_name}</span>
                          {msg.is_reply && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-700 bg-violet-50 rounded px-1.5 py-0.5">
                              <CornerUpLeft className="w-2.5 h-2.5" />
                              replied{msg.parent_sender_name ? ` to ${msg.parent_sender_name}` : ''}
                            </span>
                          )}
                          <span className="text-xs text-slate-400" title={formatISTFull(msg.created_at)}>{formatDate(msg.created_at)}</span>
                        </div>
                        {/* The message this reply answers, so the feed shows the relation */}
                        {msg.is_reply && (
                          <div className="mt-1 mb-0.5 border-l-2 border-violet-200 pl-2">
                            <p className="text-xs text-slate-500 line-clamp-2">
                              {msg.parent_content || <span className="italic text-slate-400">original message unavailable</span>}
                            </p>
                          </div>
                        )}
                        {msg.content && <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed mt-0.5 line-clamp-4">{renderMentionedContent(msg.content, msg.mentions)}</p>}
                        {msg.attachments?.length > 0 && <div onClick={e => e.stopPropagation()}>{renderAttachments(msg.attachments, false)}</div>}
                        {msg.tags && Object.keys(msg.tags).length > 0 && (
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            {TAG_META.filter(t => msg.tags[t.key]).map(t => (
                              <span key={t.key} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${t.cls}`}>{t.icon} {t.key}</span>
                            ))}
                          </div>
                        )}
                        {/* Inline reply (Activity feed) */}
                        <div className="flex items-center gap-3 mt-1.5" onClick={e => e.stopPropagation()}>
                          {msg.reply_count > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                              <MessageSquare className="w-3 h-3" />{msg.reply_count}
                            </span>
                          )}
                          <button
                            onClick={() => { setActivityReplyId(activityReplyId === msg.msg_id ? null : msg.msg_id); setActivityReplyText(''); }}
                            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-blue-600 transition-colors">
                            <CornerUpLeft className="w-3 h-3" /> Reply
                          </button>
                        </div>
                        {activityReplyId === msg.msg_id && (
                          <div className="mt-2 flex items-end gap-2" onClick={e => e.stopPropagation()}>
                            <textarea
                              value={activityReplyText}
                              onChange={e => setActivityReplyText(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleActivityReply(msg); }
                                else if (e.key === 'Escape') { setActivityReplyId(null); }
                              }}
                              placeholder="Type a reply…"
                              rows={1}
                              autoFocus
                              className="flex-1 resize-none rounded-md border border-slate-200 px-3 py-1.5 text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-blue-500 max-h-32"
                            />
                            <Button size="sm" className="h-8 px-3 shrink-0" disabled={activityReplySending || !activityReplyText.trim()} onClick={() => handleActivityReply(msg)}>
                              {activityReplySending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {activityHasMore && (
                    <div className="p-3 text-center">
                      <Button variant="outline" size="sm" disabled={activityLoading} onClick={() => fetchActivity(activityPage + 1, activityFilter)}>
                        {activityLoading ? 'Loading…' : 'Load more'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

      ) : (

        /* ── My Messages / Channels ─────────────────────────────────────────── */
        <div className="flex flex-col md:flex-row gap-3 flex-1 min-h-0">

          {/* Sidebar */}
          <Card className="w-full md:w-64 md:shrink-0 flex flex-col min-h-0 max-h-56 md:max-h-none">
            {/* Search */}
            <div className="p-2.5 border-b shrink-0">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-8 h-8 text-xs" />
              </div>
            </div>

            {/* Sidebar: Channels first, Direct Messages below */}
            <div className="overflow-y-auto flex-1">
              {/* Channels list */}
              <div className="p-2">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Channels</span>
                  <button onClick={() => setNewChannelDialog(true)} className="flex items-center gap-0.5 text-xs text-blue-600 hover:text-blue-800 font-semibold">
                    <Plus className="w-3 h-3" /> New
                  </button>
                </div>
                {filteredChannels.length === 0 && (
                  <button onClick={() => setNewChannelDialog(true)} className="w-full text-left px-2 py-2 text-xs text-blue-600 hover:bg-blue-50 rounded-md transition-colors">
                    + Create a channel
                  </button>
                )}
                {filteredChannels.map(ch => {
                  const active = activeSection === 'channels' && selectedChannel?.channel_id === ch.channel_id;
                  return (
                    <div key={ch.channel_id}
                      onClick={() => { setSelectedChannel(ch); setActiveSection('channels'); setSelectedConversation(null); }}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors mb-0.5 ${active ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>
                      <Hash className={`w-4 h-4 shrink-0 ${active ? 'text-blue-200' : 'text-slate-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate leading-tight ${ch.unread_count > 0 ? 'font-bold' : 'font-medium'}`}>{ch.name}</p>
                        {draftKeys.has(`channel:${ch.channel_id}`) ? (
                          <p className={`text-xs truncate leading-tight ${active ? 'text-blue-100' : 'text-emerald-600'}`}>
                            <span className="font-semibold">Draft: </span>{readDraft(`channel:${ch.channel_id}`).text}
                          </p>
                        ) : ch.last_message && <p className={`text-xs truncate leading-tight ${active ? 'text-blue-200' : 'text-slate-400'}`}>{ch.last_message}</p>}
                      </div>
                      {ch.unread_count > 0 && (
                        <Badge className={`text-xs h-4 px-1.5 shrink-0 ${active ? 'bg-white text-blue-600' : 'bg-blue-600 text-white'}`}>{ch.unread_count}</Badge>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* DM list */}
              <div className="p-2 border-t">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Direct Messages</span>
                  <button onClick={() => setNewChatDialog(true)} className="flex items-center gap-0.5 text-xs text-blue-600 hover:text-blue-800 font-semibold">
                    <Plus className="w-3 h-3" /> New
                  </button>
                </div>
                {filteredConversations.length === 0 && (
                  <button onClick={() => setNewChatDialog(true)} className="w-full text-left px-2 py-2 text-xs text-blue-600 hover:bg-blue-50 rounded-md transition-colors">
                    + Start a conversation
                  </button>
                )}
                {filteredConversations.map(conv => {
                  const active = activeSection === 'dm' && selectedConversation?.user_id === conv.user_id;
                  return (
                    <div key={conv.user_id}
                      onClick={() => { setSelectedConversation(conv); setActiveSection('dm'); setSelectedChannel(null); setThreadMsg(null); }}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors mb-0.5 ${active ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>
                      <Avatar className="w-7 h-7 shrink-0">
                        <AvatarFallback className={`text-xs font-medium ${active ? 'bg-blue-500 text-white' : 'bg-gradient-to-br from-blue-500 to-purple-600 text-white'}`}>
                          {getInitials(conv.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate leading-tight ${conv.unread_count > 0 ? 'font-bold' : 'font-medium'}`}>{conv.name}</p>
                        {draftKeys.has(`dm:${conv.user_id}`) ? (
                          <p className={`text-xs truncate leading-tight ${active ? 'text-blue-100' : 'text-emerald-600'}`}>
                            <span className="font-semibold">Draft: </span>{readDraft(`dm:${conv.user_id}`).text}
                          </p>
                        ) : conv.last_message && <p className={`text-xs truncate leading-tight ${active ? 'text-blue-200' : 'text-slate-400'}`}>{conv.last_message}</p>}
                      </div>
                      {conv.unread_count > 0 && (
                        <Badge className={`text-xs h-4 px-1.5 shrink-0 ${active ? 'bg-white text-blue-600' : 'bg-blue-600 text-white'}`}>{conv.unread_count}</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* Main content */}
          <div className="flex-1 flex gap-3 min-w-0">

            {/* Channel / DM area */}
            <Card className="flex-1 flex flex-col min-w-0">

              {/* ── CHANNELS ─────────────────────────────────────────────── */}
              {activeSection === 'channels' && selectedChannel ? (
                <>
                  {/* Channel header */}
                  <div className="flex items-center gap-3 px-4 py-3 border-b shrink-0">
                    <Hash className="w-5 h-5 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800 leading-tight">{selectedChannel.name}</h3>
                      {selectedChannel.description && <p className="text-xs text-slate-500 truncate">{selectedChannel.description}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-400 shrink-0">
                      <Users className="w-4 h-4" />
                      <span className="text-xs font-medium mr-1">{selectedChannel.members?.length || 0}</span>
                      <button className="p-1 hover:bg-slate-100 rounded transition-colors" onClick={() => sendBuzz('channel', selectedChannel.channel_id, `# ${selectedChannel.name}`)} title="Buzz everyone in this channel">
                        <PhoneCall className="w-4 h-4 text-slate-400 hover:text-blue-600" />
                      </button>
                      <button className="p-1 hover:bg-slate-100 rounded transition-colors" onClick={openEditDialog} title="Edit channel">
                        <Settings className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                      </button>
                      {threadMsg && (
                        <button className="p-1 hover:bg-slate-100 rounded" onClick={() => { setThreadMsg(null); setThreadReplies([]); }}>
                          <PanelRightOpen className="w-4 h-4 text-slate-500" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Channel search + filter */}
                  <div className="flex items-center gap-2 px-4 py-2 border-b bg-slate-50/60 shrink-0">
                    <div className="relative flex-1 max-w-xs">
                      <SearchIcon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input value={msgSearch} onChange={e => setMsgSearch(e.target.value)} placeholder="Search messages…"
                        className="w-full pl-8 pr-2 h-8 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-blue-300" />
                    </div>
                    <select value={msgFilter} onChange={e => setMsgFilter(e.target.value)}
                      className="h-8 text-xs rounded-md border border-slate-200 bg-white px-2 text-slate-600">
                      <option value="all">All</option>
                      <option value="media">Media</option>
                      <option value="links">Links</option>
                    </select>
                    <div className="flex items-center gap-1">
                      <input type="date" value={msgDateFrom} onChange={e => setMsgDateFrom(e.target.value)} max={msgDateTo || undefined}
                        title="From date" className="h-8 text-xs rounded-md border border-slate-200 bg-white px-2 text-slate-600" />
                      <span className="text-slate-400 text-xs">–</span>
                      <input type="date" value={msgDateTo} onChange={e => setMsgDateTo(e.target.value)} min={msgDateFrom || undefined}
                        title="To date" className="h-8 text-xs rounded-md border border-slate-200 bg-white px-2 text-slate-600" />
                    </div>
                    {(msgSearch || msgFilter !== 'all' || msgDateFrom || msgDateTo) && (
                      <button onClick={() => { setMsgSearch(''); setMsgFilter('all'); setMsgDateFrom(''); setMsgDateTo(''); }} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-0.5"><X className="w-3.5 h-3.5" />Clear</button>
                    )}
                  </div>

                  {/* Channel messages */}
                  <div ref={channelScrollRef} onScroll={handleChannelScroll} className="relative flex-1 overflow-y-auto py-2">
                    {channelLoading && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[1px]">
                        <div className="flex items-center gap-2 text-slate-500 text-sm">
                          <Loader2 className="w-4 h-4 animate-spin" /> Loading messages...
                        </div>
                      </div>
                    )}
                    {channelLoadingMore && (
                      <div className="flex items-center justify-center py-2 text-slate-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </div>
                    )}
                    {channelMessages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-slate-400">
                        <Hash className="w-12 h-12 mb-3 opacity-20" />
                        {msgSearch ? (
                          <>
                            <p className="font-medium text-slate-600">No messages match "{msgSearch}"</p>
                            <p className="text-sm mt-1">Searches the whole channel, not just what's loaded</p>
                          </>
                        ) : (msgDateFrom || msgDateTo) ? (
                          <>
                            <p className="font-medium text-slate-600">No messages in this range</p>
                            <p className="text-sm mt-1">Try a different date range</p>
                          </>
                        ) : (
                          <>
                            <p className="font-medium text-slate-600">No messages yet</p>
                            <p className="text-sm mt-1">Be the first to say something in #{selectedChannel.name}</p>
                          </>
                        )}
                      </div>
                    ) : (
                      groupedChannelMessages.map(msg => (
                        <ChannelMessageRow
                          key={msg.msg_id}
                          msg={msg}
                          currentUserId={user?.user_id}
                          isAdmin={isAdmin}
                          isEditing={editingId === msg.msg_id}
                          editText={editingId === msg.msg_id ? editText : ''}
                          onEditTextChange={onEditTextChange}
                          highlighted={highlightMsgId === msg.msg_id}
                          tagChannelActive={TAG_CHANNELS.includes(selectedChannel?.name)}
                          canProcessTx={canProcessTx}
                          onOpenThread={openThreadForMsg}
                          onReact={handleReact}
                          onTag={handleTag}
                          onStartEdit={startEdit}
                          onSaveEdit={saveEditChannel}
                          onCancelEdit={cancelEdit}
                          onDelete={deleteChannelMsg}
                          onTxProcess={handleTxProcess}
                          onTxComplete={handleTxComplete}
                          onNavigateToTx={navigateToTx}
                          onImageClick={setLightboxUrl}
                        />
                      ))
                    )}
                  </div>

                  {/* Channel compose */}
                  <ChannelComposer
                    channelId={selectedChannel.channel_id}
                    channelName={selectedChannel.name}
                    members={channelMentionCandidates}
                    onSent={handleChannelMessageSent}
                    getAuthHeaders={getAuthHeaders}
                  />
                </>

              ) : activeSection === 'dm' && selectedConversation ? (
                /* ── DM VIEW ───────────────────────────────────────────────── */
                <>
                  {/* DM header */}
                  <div className="flex items-center gap-3 px-4 py-3 border-b shrink-0">
                    <Avatar className="w-9 h-9">
                      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-medium">
                        {getInitials(selectedConversation.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800 leading-tight">{selectedConversation.name}</h3>
                      <p className="text-xs text-slate-400">{selectedConversation.email}</p>
                    </div>
                    <button className="p-1.5 hover:bg-slate-100 rounded-full transition-colors shrink-0" onClick={() => sendBuzz('dm', selectedConversation.user_id, selectedConversation.name)} title={`Buzz ${selectedConversation.name}`}>
                      <PhoneCall className="w-4 h-4 text-slate-400 hover:text-blue-600" />
                    </button>
                    <Badge variant="outline" className="text-xs shrink-0">{selectedConversation.role}</Badge>
                  </div>

                  {/* DM search + filter */}
                  <div className="flex items-center gap-2 px-4 py-2 border-b bg-slate-50/60 shrink-0">
                    <div className="relative flex-1 max-w-xs">
                      <SearchIcon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input value={msgSearch} onChange={e => setMsgSearch(e.target.value)} placeholder="Search messages…"
                        className="w-full pl-8 pr-2 h-8 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-blue-300" />
                    </div>
                    <select value={msgFilter} onChange={e => setMsgFilter(e.target.value)}
                      className="h-8 text-xs rounded-md border border-slate-200 bg-white px-2 text-slate-600">
                      <option value="all">All</option>
                      <option value="media">Media</option>
                      <option value="links">Links</option>
                    </select>
                    <div className="flex items-center gap-1">
                      <input type="date" value={msgDateFrom} onChange={e => setMsgDateFrom(e.target.value)} max={msgDateTo || undefined}
                        title="From date" className="h-8 text-xs rounded-md border border-slate-200 bg-white px-2 text-slate-600" />
                      <span className="text-slate-400 text-xs">–</span>
                      <input type="date" value={msgDateTo} onChange={e => setMsgDateTo(e.target.value)} min={msgDateFrom || undefined}
                        title="To date" className="h-8 text-xs rounded-md border border-slate-200 bg-white px-2 text-slate-600" />
                    </div>
                    {(msgSearch || msgFilter !== 'all' || msgDateFrom || msgDateTo) && (
                      <button onClick={() => { setMsgSearch(''); setMsgFilter('all'); setMsgDateFrom(''); setMsgDateTo(''); }} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-0.5"><X className="w-3.5 h-3.5" />Clear</button>
                    )}
                  </div>

                  {/* DM messages */}
                  <div ref={dmScrollRef} onScroll={handleDmScroll} className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
                    {messages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-slate-400">
                        <MessageSquare className="w-12 h-12 mb-3 opacity-20" />
                        <p className="font-medium text-slate-600">No messages yet</p>
                        <p className="text-sm mt-1">Start the conversation</p>
                      </div>
                    ) : messages.filter(msgMatches).map((msg, i, arr) => {
                      const isSelf = msg.sender_id === user?.user_id;
                      const prev = arr[i - 1];
                      const sameDay = prev && new Date(msg.created_at).toDateString() === new Date(prev.created_at).toDateString();
                      return (
                        <div key={msg.message_id}>
                          {!sameDay && prev && <DateDivider date={msg.created_at} />}
                          {!prev && <DateDivider date={msg.created_at} />}
                          <div className={`flex items-end gap-2 ${isSelf ? 'justify-end' : 'justify-start'}`}>
                            {!isSelf && (
                              <Avatar className="w-7 h-7 shrink-0 mb-1">
                                <AvatarFallback className="bg-gradient-to-br from-violet-500 to-purple-600 text-white text-xs">
                                  {getInitials(msg.sender_name)}
                                </AvatarFallback>
                              </Avatar>
                            )}
                            <div className={`flex flex-col min-w-0 ${isSelf ? 'items-end' : 'items-start'}`}>
                            {!msg.deleted && (
                              <ReactionChips reactions={msg.reactions} currentUserId={user?.user_id}
                                onReact={(emoji) => handleReact(msg, emoji, 'dm')} />
                            )}
                            {msg.deleted ? (
                              <div className="max-w-[70%] rounded-2xl px-4 py-2 bg-slate-100 text-slate-400 italic text-sm">🚫 This message was deleted</div>
                            ) : editingId === msg.message_id ? (
                              <div className="max-w-[70%] w-full flex flex-col gap-1">
                                <textarea value={editText} onChange={e => setEditText(e.target.value)} rows={2}
                                  className="text-sm rounded-lg border border-blue-300 px-3 py-2 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-400" autoFocus />
                                <div className="flex gap-2 justify-end">
                                  <button onClick={() => saveEditDM(msg.message_id)} className="text-xs bg-blue-600 text-white rounded px-2 py-0.5 hover:bg-blue-700">Save</button>
                                  <button onClick={cancelEdit} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
                                </div>
                              </div>
                            ) : (
                            <div className={`relative group/msg max-w-[70%] rounded-2xl px-4 py-2.5 ${isSelf ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-slate-100 text-slate-800 rounded-bl-sm'}`}>
                              {(isSelf || isAdmin) && (
                                <div className="absolute -top-2 -right-2 opacity-0 group-hover/msg:opacity-100 transition-opacity flex gap-0.5 bg-white border border-slate-200 rounded-full shadow-sm px-1 z-10">
                                  {isSelf && <button title="Edit" onClick={() => startEdit(msg.message_id, msg.content)} className="p-1 text-slate-500 hover:text-blue-600"><Pencil className="w-3 h-3" /></button>}
                                  <button title="Delete" onClick={() => deleteDM(msg.message_id)} className="p-1 text-slate-500 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                                </div>
                              )}
                              {msg.content && (msg.link_channel_id ? (
                                <p onClick={() => jumpToChannelMessage(msg.link_channel_id, msg.link_msg_id, msg.link_thread)}
                                  title="Open the thread" className="text-sm leading-relaxed cursor-pointer hover:underline underline-offset-2">
                                  {renderMentionedContent(msg.content, msg.mentions)} <span className="opacity-70">↗</span>
                                </p>
                              ) : (
                                <p className="text-sm leading-relaxed">{renderMentionedContent(msg.content, msg.mentions)}</p>
                              ))}
                              {msg.attachment && (
                                isImage(msg.attachment.filename, msg.attachment.content_type) ? (
                                  <div className="relative group mt-1 inline-block cursor-pointer rounded-lg overflow-hidden" onClick={() => setLightboxUrl(msg.attachment.url || `${API_URL}/api/messages/attachment/${msg.message_id}`)}>
                                    <img src={msg.attachment.url || `${API_URL}/api/messages/attachment/${msg.message_id}`}
                                      alt={msg.attachment.filename} className="rounded-lg max-h-48 object-cover" style={{ maxWidth: 240 }} />
                                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                      <ZoomIn className="w-6 h-6 text-white drop-shadow" />
                                    </div>
                                  </div>
                                ) : (
                                  <a href="#" onClick={async e => {
                                    e.preventDefault();
                                    const token = localStorage.getItem('auth_token');
                                    const r = await fetch(`${API_URL}/api/messages/attachment/${msg.message_id}`, { headers: { Authorization: `Bearer ${token}` } });
                                    const blob = await r.blob(); const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a'); a.href = url; a.download = msg.attachment.filename; a.click(); URL.revokeObjectURL(url);
                                  }} className={`flex items-center gap-2 mt-1 p-2 rounded-lg cursor-pointer ${isSelf ? 'bg-blue-500/30 hover:bg-blue-500/50' : 'bg-slate-200 hover:bg-slate-300'}`}>
                                    {getFileIcon(msg.attachment.filename, msg.attachment.content_type)}
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-medium truncate">{msg.attachment.filename}</p>
                                      <p className={`text-xs ${isSelf ? 'text-blue-200' : 'text-slate-400'}`}>{formatFileSize(msg.attachment.size)}</p>
                                    </div>
                                  </a>
                                )
                              )}
                              {msg.is_tx_bot && (
                                <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                                  <TxBadge msg={msg} />
                                  {msg.tx_reference && (
                                    <button type="button" onClick={() => navigate(`/transactions?search=${encodeURIComponent(msg.tx_reference)}`)}
                                      className={`text-[11px] underline ${isSelf ? 'text-blue-100' : 'text-blue-600'} hover:opacity-80`}>
                                      View transaction →
                                    </button>
                                  )}
                                  {msg.tx_processed_by && (
                                    <span title={txBadgeTip(msg)} className="text-[11px] text-emerald-600 font-medium cursor-help">🟢 Processed{msg.tx_processed_by_name ? ` by ${msg.tx_processed_by_name}` : ''}</span>
                                  )}
                                  {msg.tx_owner_id === user?.user_id && !msg.tx_completed_by && (
                                    <button type="button" onClick={() => handleTxComplete(msg)}
                                      className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-600 text-white hover:bg-blue-700">Complete</button>
                                  )}
                                  {msg.tx_completed_by && (
                                    <span title={txBadgeTip(msg)} className="text-[11px] text-green-600 font-medium cursor-help">✅ Completed{msg.tx_completed_by_name ? ` by ${msg.tx_completed_by_name}` : ''}</span>
                                  )}
                                </div>
                              )}
                              <div className={`flex items-center justify-end gap-1 mt-0.5 ${isSelf ? 'text-blue-200' : 'text-slate-400'}`}>
                                {msg.edited && <span className="text-[10px] opacity-80 mr-0.5">(edited)</span>}
                                <span className="text-xs" title={formatISTFull(msg.created_at)}>{formatTime(msg.created_at)}</span>
                                {isSelf && (
                                  msg.read
                                    ? <CheckCheck className="w-3.5 h-3.5 text-blue-100" />
                                    : <Check className="w-3.5 h-3.5 text-blue-300 opacity-70" />
                                )}
                              </div>
                            </div>
                            )}
                            {!msg.deleted && (
                              <ReactionAdder onReact={(emoji) => handleReact(msg, emoji, 'dm')} />
                            )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* DM compose */}
                  <div className="px-4 py-3 border-t shrink-0">
                    {attachment && (
                      <div className="flex items-center gap-2 mb-2 p-2 bg-slate-50 rounded-lg border">
                        {getFileIcon(attachment.name, attachment.type)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate">{attachment.name}</p>
                          <p className="text-xs text-slate-400">{formatFileSize(attachment.size)}</p>
                        </div>
                        <button onClick={() => setAttachment(null)} className="text-slate-400 hover:text-red-500 transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <div className="relative flex items-end gap-2 bg-slate-100/70 rounded-xl px-3 py-2 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-400/40 transition-all border border-transparent focus-within:border-blue-300">
                      {dmMention.trigger && (
                        <MentionDropdown candidates={dmMention.candidates} activeIndex={dmMention.activeIndex}
                          onSelect={u => {
                            const result = dmMention.applyMention(u, newMessage);
                            if (result) {
                              updateNewMessage(result.text);
                              requestAnimationFrame(() => { dmTextareaRef.current?.focus(); dmTextareaRef.current?.setSelectionRange(result.caret, result.caret); });
                            }
                          }} />
                      )}
                      <input ref={fileInputRef} type="file" className="hidden"
                        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.xlsx,.xls,.csv,.doc,.docx"
                        onChange={handleDmFileSelect} />
                      <button className="text-slate-400 hover:text-blue-600 transition-colors shrink-0" onClick={() => fileInputRef.current?.click()} title="Attach file">
                        <Paperclip className="w-5 h-5" />
                      </button>
                      <Input
                        ref={dmTextareaRef}
                        value={newMessage}
                        onChange={e => {
                          updateNewMessage(e.target.value);
                          dmMention.handleChange(e.target.value, e.target.selectionStart);
                        }}
                        placeholder={`Message ${selectedConversation.name} (@ to mention)`}
                        onKeyDown={e => {
                          if (dmMention.handleKeyDown(e, newMessage, updateNewMessage, dmTextareaRef.current)) return;
                          if (e.key === 'Enter' && !e.shiftKey) handleSendMessage();
                        }}
                        className="flex-1 border-0 p-0 focus-visible:ring-0 shadow-none text-sm bg-transparent"
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={(!newMessage.trim() && !attachment) || sending}
                        className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
                      >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </>

              ) : (
                /* ── Empty state ─────────────────────────────────────────── */
                <div className="flex-1 flex items-center justify-center text-slate-400">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-20" />
                    <h3 className="text-lg font-medium text-slate-600">Select a conversation or channel</h3>
                    <p className="text-sm mt-1">Choose from the sidebar to start messaging</p>
                  </div>
                </div>
              )}
            </Card>

            {/* Thread panel */}
            {threadMsg && selectedChannel && (
              <Card className="w-full md:w-80 md:shrink-0 flex flex-col min-h-0">
                <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
                  <div>
                    <h3 className="font-semibold text-slate-800 text-sm">Thread</h3>
                    <p className="text-xs text-slate-400">#{selectedChannel.name}</p>
                  </div>
                  <button onClick={() => { setThreadMsg(null); setThreadReplies([]); }} className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div ref={threadScrollRef} className="flex-1 overflow-y-auto p-4">
                  {/* Root message */}
                  <div className="mb-4 pb-4 border-b">
                    <div className="group flex gap-2.5">
                      <Avatar className="w-8 h-8 shrink-0">
                        <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-xs">
                          {getInitials(threadMsg.sender_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 cursor-pointer" title="Jump to this message in the channel"
                          onClick={() => scrollToChannelMessage(threadMsg.msg_id)}>
                          <span className="text-sm font-bold text-slate-900">{threadMsg.sender_id === user?.user_id ? 'You' : threadMsg.sender_name}</span>
                          <span className="text-xs text-slate-400 whitespace-nowrap" title={formatISTFull(threadMsg.created_at)}>{formatDate(threadMsg.created_at)}</span>
                        </div>
                        {TAG_CHANNELS.includes(selectedChannel?.name) && (
                          <TagChips tags={threadMsg.tags} onTag={(tag) => handleTag(threadMsg, tag)} currentUserId={user?.user_id} />
                        )}
                        {threadMsg.content && (
                          <p title="Jump to this message in the channel" onClick={() => scrollToChannelMessage(threadMsg.msg_id)}
                            className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap leading-relaxed cursor-pointer rounded hover:bg-slate-100 transition-colors">
                            {renderMentionedContent(threadMsg.content, threadMsg.mentions)}
                          </p>
                        )}
                        {renderAttachments(threadMsg.attachments, threadMsg.sender_id === user?.user_id)}
                        {TAG_CHANNELS.includes(selectedChannel?.name) && (
                          <TagBar tags={threadMsg.tags} onTag={(tag) => handleTag(threadMsg, tag)} currentUserId={user?.user_id} />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Replies */}
                  <p className="text-xs text-slate-400 font-semibold mb-3">
                    {threadReplies.length} {threadReplies.length === 1 ? 'reply' : 'replies'}
                  </p>
                  {threadLoading && (
                    <div className="flex items-center gap-2 justify-center py-3 text-slate-400 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading replies...
                    </div>
                  )}
                  {threadReplies.map(r => (
                    <div key={r.msg_id} className="group flex gap-2.5 mb-3">
                      <Avatar className="w-7 h-7 shrink-0">
                        <AvatarFallback className="bg-gradient-to-br from-blue-400 to-cyan-500 text-white text-xs">
                          {getInitials(r.sender_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0 group/rep">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs font-bold text-slate-900">{r.sender_id === user?.user_id ? 'You' : r.sender_name}</span>
                          <span className="text-xs text-slate-400 whitespace-nowrap" title={formatISTFull(r.created_at)}>{formatDate(r.created_at)}</span>
                          {r.edited && !r.deleted && <span className="text-[10px] text-slate-400">(edited)</span>}
                          {(r.sender_id === user?.user_id || isAdmin) && !r.deleted && (
                            <span className="opacity-0 group-hover/rep:opacity-100 transition-opacity flex gap-0.5">
                              {r.sender_id === user?.user_id && <button title="Edit" onClick={() => startEdit(r.msg_id, r.content)} className="p-0.5 text-slate-400 hover:text-blue-600"><Pencil className="w-3 h-3" /></button>}
                              <button title="Delete" onClick={() => deleteReply(r.msg_id)} className="p-0.5 text-slate-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                            </span>
                          )}
                        </div>
                        {!r.deleted && (
                          <ReactionChips reactions={r.reactions} currentUserId={user?.user_id}
                            onReact={(emoji) => handleReact(r, emoji, 'channel')} />
                        )}
                        {!r.deleted && TAG_CHANNELS.includes(selectedChannel?.name) && (
                          <TagChips tags={r.tags} onTag={(tag) => handleTag(r, tag)} currentUserId={user?.user_id} />
                        )}
                        {r.deleted ? (
                          <p className="text-sm text-slate-400 italic mt-0.5">🚫 This message was deleted</p>
                        ) : editingId === r.msg_id ? (
                          <div className="flex flex-col gap-1 mt-1">
                            <textarea value={editText} onChange={e => setEditText(e.target.value)} rows={2}
                              className="text-sm rounded-lg border border-blue-300 px-3 py-2 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-400" autoFocus />
                            <div className="flex gap-2">
                              <button onClick={() => saveEditReply(r.msg_id)} className="text-xs bg-blue-600 text-white rounded px-2 py-0.5 hover:bg-blue-700">Save</button>
                              <button onClick={cancelEdit} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {r.content && <p className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap leading-relaxed">{renderMentionedContent(r.content, r.mentions)}</p>}
                            {renderAttachments(r.attachments, r.sender_id === user?.user_id)}
                          </>
                        )}
                        {!r.deleted && (
                          <div className="flex items-center gap-1.5 clear-both">
                            <ReactionAdder onReact={(emoji) => handleReact(r, emoji, 'channel')} />
                            {TAG_CHANNELS.includes(selectedChannel?.name) && (
                              <TagBar tags={r.tags} onTag={(tag) => handleTag(r, tag)} currentUserId={user?.user_id} />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Thread compose */}
                <div className="px-3 py-3 border-t shrink-0">
                  <FilePreviewStrip files={threadFiles} onRemove={i => setThreadFiles(prev => prev.filter((_, idx) => idx !== i))} />
                  <div className="relative flex items-end gap-2 bg-slate-100/70 rounded-xl px-2.5 py-1.5 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-400/40 transition-all border border-transparent focus-within:border-blue-300">
                    {threadMention.trigger && (
                      <MentionDropdown candidates={threadMention.candidates} activeIndex={threadMention.activeIndex}
                        onSelect={u => {
                          const result = threadMention.applyMention(u, threadText);
                          if (result) {
                            updateThreadText(result.text);
                            requestAnimationFrame(() => { threadTextareaRef.current?.focus(); threadTextareaRef.current?.setSelectionRange(result.caret, result.caret); });
                          }
                        }} />
                    )}
                    <input ref={threadFileInputRef} type="file" className="hidden" multiple accept="image/*,video/*,.pdf,.xlsx,.xls,.csv,.doc,.docx" onChange={handleThreadFileSelect} />
                    <button className="text-slate-400 hover:text-blue-600 shrink-0" onClick={() => threadFileInputRef.current?.click()}>
                      <Paperclip className="w-4 h-4" />
                    </button>
                    <Textarea
                      ref={threadTextareaRef}
                      value={threadText}
                      onChange={e => {
                        updateThreadText(e.target.value);
                        threadMention.handleChange(e.target.value, e.target.selectionStart);
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px';
                      }}
                      onPaste={handleThreadPaste}
                      onKeyDown={e => {
                        if (threadMention.handleKeyDown(e, threadText, updateThreadText, threadTextareaRef.current)) return;
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendThreadReply(); }
                      }}
                      placeholder="Reply in thread… (@ to mention)"
                      className="flex-1 resize-none text-sm border-0 p-0 focus-visible:ring-0 shadow-none bg-transparent overflow-y-hidden"
                      rows={1}
                      style={{ minHeight: 20, maxHeight: 96 }}
                    />
                    <button onClick={handleSendThreadReply} disabled={(!threadText.trim() && !threadFiles.length) || sendingThread}
                      className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white transition-colors">
                      {sendingThread ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* New DM dialog */}
      <Dialog open={newChatDialog} onOpenChange={setNewChatDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> New Direct Message</DialogTitle></DialogHeader>
          <div>
            <Label>Select User</Label>
            <Select value={selectedRecipient} onValueChange={setSelectedRecipient}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Choose a user to message..." />
              </SelectTrigger>
              <SelectContent>
                {users.map(u => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>{u.name}</span>
                      <Badge variant="outline" className="ml-2 text-xs">{u.role}</Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewChatDialog(false)}>Cancel</Button>
            <Button onClick={handleStartNewChat} disabled={!selectedRecipient}>
              <MessageSquare className="w-4 h-4 mr-2" /> Start Chat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Channel dialog */}
      <Dialog open={newChannelDialog} onOpenChange={v => { setNewChannelDialog(v); if (!v) { setChannelName(''); setChannelDesc(''); setChannelMembers([]); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Hash className="w-5 h-5" /> Create Channel</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Channel Name *</Label>
              <Input className="mt-1" placeholder="e.g. general, ops-team, finance"
                value={channelName}
                onChange={e => setChannelName(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))} />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input className="mt-1" placeholder="What's this channel about?" value={channelDesc} onChange={e => setChannelDesc(e.target.value)} />
            </div>
            <div>
              <Label>Add Members</Label>
              <div className="mt-1 max-h-40 overflow-y-auto border rounded-md p-2 space-y-0.5">
                {users.map(u => (
                  <label key={u.user_id} className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-slate-50 rounded">
                    <input type="checkbox" className="rounded"
                      checked={channelMembers.includes(u.user_id)}
                      onChange={e => setChannelMembers(prev => e.target.checked ? [...prev, u.user_id] : prev.filter(id => id !== u.user_id))} />
                    <span className="text-sm">{u.name}</span>
                    <Badge variant="outline" className="text-xs ml-auto">{u.role}</Badge>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1">You are automatically added as a member.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewChannelDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateChannel} disabled={!channelName.trim() || creatingChannel}>
              {creatingChannel ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Hash className="w-4 h-4 mr-2" />}
              Create Channel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Channel dialog */}
      <Dialog open={editChannelDialog} onOpenChange={v => { setEditChannelDialog(v); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5" /> Edit Channel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Channel Name *</Label>
              <Input className="mt-1" placeholder="e.g. general, ops-team"
                value={editChannelName}
                onChange={e => setEditChannelName(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))} />
            </div>
            <div>
              <Label>Description</Label>
              <Input className="mt-1" placeholder="What's this channel about?" value={editChannelDesc} onChange={e => setEditChannelDesc(e.target.value)} />
            </div>
            <div>
              <Label>Members</Label>
              <div className="mt-1 max-h-40 overflow-y-auto border rounded-md p-2 space-y-0.5">
                {users.map(u => (
                  <label key={u.user_id} className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-slate-50 rounded">
                    <input type="checkbox" className="rounded"
                      checked={editChannelMembers.includes(u.user_id)}
                      onChange={e => setEditChannelMembers(prev => e.target.checked ? [...prev, u.user_id] : prev.filter(id => id !== u.user_id))} />
                    <span className="text-sm">{u.name}</span>
                    <Badge variant="outline" className="text-xs ml-auto">{u.role}</Badge>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1">Uncheck to remove members.</p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <button
              onClick={handleDeleteChannel}
              className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-md transition-colors mr-auto"
            >
              <Trash2 className="w-4 h-4" /> Delete Channel
            </button>
            <Button variant="outline" onClick={() => setEditChannelDialog(false)}>Cancel</Button>
            <Button onClick={handleEditChannel} disabled={!editChannelName.trim() || savingChannel}>
              {savingChannel ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Settings className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      {lightboxUrl && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={() => setLightboxUrl(null)}>
          <div className="relative max-w-5xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <img src={lightboxUrl} alt="Full size" className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" />
            <button className="absolute -top-3 -right-3 w-8 h-8 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center" onClick={() => setLightboxUrl(null)}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
